// src/storefront/http-client.ts
var ContentType = /* @__PURE__ */ ((ContentType4) => {
  ContentType4["Json"] = "application/json";
  ContentType4["FormData"] = "multipart/form-data";
  ContentType4["UrlEncoded"] = "application/x-www-form-urlencoded";
  ContentType4["Text"] = "text/plain";
  return ContentType4;
})(ContentType || {});
var HttpClient = class {
  constructor(apiConfig = {}) {
    this.baseUrl = "//willys.local.se:9002";
    this.securityData = null;
    this.abortControllers = /* @__PURE__ */ new Map();
    this.customFetch = (...fetchParams) => fetch(...fetchParams);
    this.baseApiParams = {
      credentials: "same-origin",
      headers: {},
      redirect: "follow",
      referrerPolicy: "no-referrer"
    };
    this.setSecurityData = (data) => {
      this.securityData = data;
    };
    this.contentFormatters = {
      ["application/json" /* Json */]: (input) => input !== null && (typeof input === "object" || typeof input === "string") ? JSON.stringify(input) : input,
      ["text/plain" /* Text */]: (input) => input !== null && typeof input !== "string" ? JSON.stringify(input) : input,
      ["multipart/form-data" /* FormData */]: (input) => Object.keys(input || {}).reduce((formData, key) => {
        const property = input[key];
        formData.append(
          key,
          property instanceof Blob ? property : typeof property === "object" && property !== null ? JSON.stringify(property) : `${property}`
        );
        return formData;
      }, new FormData()),
      ["application/x-www-form-urlencoded" /* UrlEncoded */]: (input) => this.toQueryString(input)
    };
    this.createAbortSignal = (cancelToken) => {
      if (this.abortControllers.has(cancelToken)) {
        const abortController2 = this.abortControllers.get(cancelToken);
        if (abortController2) {
          return abortController2.signal;
        }
        return void 0;
      }
      const abortController = new AbortController();
      this.abortControllers.set(cancelToken, abortController);
      return abortController.signal;
    };
    this.abortRequest = (cancelToken) => {
      const abortController = this.abortControllers.get(cancelToken);
      if (abortController) {
        abortController.abort();
        this.abortControllers.delete(cancelToken);
      }
    };
    this.request = async ({
      body,
      secure,
      path,
      type,
      query,
      format,
      baseUrl,
      cancelToken,
      ...params
    }) => {
      const secureParams = (typeof secure === "boolean" ? secure : this.baseApiParams.secure) && this.securityWorker && await this.securityWorker(this.securityData) || {};
      const requestParams = this.mergeRequestParams(params, secureParams);
      const queryString = query && this.toQueryString(query);
      const payloadFormatter = this.contentFormatters[type || "application/json" /* Json */];
      const responseFormat = format || requestParams.format;
      return this.customFetch(`${baseUrl || this.baseUrl || ""}${path}${queryString ? `?${queryString}` : ""}`, {
        ...requestParams,
        headers: {
          ...requestParams.headers || {},
          ...type && type !== "multipart/form-data" /* FormData */ ? { "Content-Type": type } : {}
        },
        signal: (cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal) || null,
        body: typeof body === "undefined" || body === null ? null : payloadFormatter(body)
      }).then(async (response) => {
        const r = response.clone();
        r.data = null;
        r.error = null;
        const data = !responseFormat ? r : await response[responseFormat]().then((data2) => {
          if (r.ok) {
            r.data = data2;
          } else {
            r.error = data2;
          }
          return r;
        }).catch((e) => {
          r.error = e;
          return r;
        });
        if (cancelToken) {
          this.abortControllers.delete(cancelToken);
        }
        if (!response.ok) throw data;
        return data;
      });
    };
    Object.assign(this, apiConfig);
  }
  encodeQueryParam(key, value) {
    const encodedKey = encodeURIComponent(key);
    return `${encodedKey}=${encodeURIComponent(typeof value === "number" ? value : `${value}`)}`;
  }
  addQueryParam(query, key) {
    return this.encodeQueryParam(key, query[key]);
  }
  addArrayQueryParam(query, key) {
    const value = query[key];
    return value.map((v) => this.encodeQueryParam(key, v)).join("&");
  }
  toQueryString(rawQuery) {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter((key) => "undefined" !== typeof query[key]);
    return keys.map((key) => Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key)).join("&");
  }
  addQueryParams(rawQuery) {
    const queryString = this.toQueryString(rawQuery);
    return queryString ? `?${queryString}` : "";
  }
  mergeRequestParams(params1, params2) {
    return {
      ...this.baseApiParams,
      ...params1,
      ...params2 || {},
      headers: {
        ...this.baseApiParams.headers || {},
        ...params1.headers || {},
        ...params2 && params2.headers || {}
      }
    };
  }
};

// src/storefront/TwoTilesButtonAndProductBannerComponent.ts
var TwoTilesButtonAndProductBannerComponent = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Component
     * @name GetProductsForProductBannerUsingGet2
     * @summary Get products for product banner
     * @request GET:/twoTilesButtonAndProductBannerComponent/{componentId}
     * @secure
     */
    this.getProductsForProductBannerUsingGet2 = (componentId, query, params = {}) => this.request({
      path: `/twoTilesButtonAndProductBannerComponent/${componentId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/Singlestepcheckout.ts
var Singlestepcheckout = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Checkout
     * @name DisplayCheckoutPageUsingGet
     * @summary Display checkout page
     * @request GET:/singlestepcheckout
     * @secure
     */
    this.displayCheckoutPageUsingGet = (params = {}) => this.request({
      path: `/singlestepcheckout`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingGet1
     * @summary Cancel payment
     * @request GET:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingGet1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingHead1
     * @summary Cancel payment
     * @request HEAD:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingHead1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "HEAD",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingPost1
     * @summary Cancel payment
     * @request POST:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingPost1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingPut1
     * @summary Cancel payment
     * @request PUT:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingPut1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "PUT",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingDelete1
     * @summary Cancel payment
     * @request DELETE:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingDelete1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingOptions1
     * @summary Cancel payment
     * @request OPTIONS:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingOptions1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "OPTIONS",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelPaymentUsingPatch1
     * @summary Cancel payment
     * @request PATCH:/singlestepcheckout/cancelPayment/{orderCode}
     * @secure
     */
    this.cancelPaymentUsingPatch1 = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/cancelPayment/${orderCode}`,
      method: "PATCH",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingGet
     * @summary Complete payment for order
     * @request GET:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingGet = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingHead
     * @summary Complete payment for order
     * @request HEAD:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingHead = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "HEAD",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingPost
     * @summary Complete payment for order
     * @request POST:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingPost = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingPut
     * @summary Complete payment for order
     * @request PUT:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingPut = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingDelete
     * @summary Complete payment for order
     * @request DELETE:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingDelete = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "DELETE",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingOptions
     * @summary Complete payment for order
     * @request OPTIONS:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingOptions = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "OPTIONS",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CompletePaymentUsingPatch
     * @summary Complete payment for order
     * @request PATCH:/singlestepcheckout/completePayment/{orderCode}
     * @secure
     */
    this.completePaymentUsingPatch = (orderCode, query, params = {}) => this.request({
      path: `/singlestepcheckout/completePayment/${orderCode}`,
      method: "PATCH",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name OrderConfirmationUsingGet
     * @summary Get order confirmation
     * @request GET:/singlestepcheckout/orderconfirmation/{orderCode}
     * @secure
     */
    this.orderConfirmationUsingGet = (orderCode, params = {}) => this.request({
      path: `/singlestepcheckout/orderconfirmation/${orderCode}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name SimplifiedPlaceOrderUsingPost
     * @summary Place order
     * @request POST:/singlestepcheckout/placeOrder
     * @secure
     */
    this.simplifiedPlaceOrderUsingPost = (data, params = {}) => this.request({
      path: `/singlestepcheckout/placeOrder`,
      method: "POST",
      body: data,
      secure: true,
      type: "application/x-www-form-urlencoded" /* UrlEncoded */,
      ...params
    });
  }
};

// src/storefront/Customers.ts
var Customers = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Customer
     * @name RegisterCustomerUsingPost
     * @summary Register customer
     * @request POST:/customers
     * @secure
     */
    this.registerCustomerUsingPost = (form, query, params = {}) => this.request({
      path: `/customers`,
      method: "POST",
      query,
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name RegisterAdditionalUsingPost
     * @summary Register additional subaccount
     * @request POST:/customers/subaccount
     * @secure
     */
    this.registerAdditionalUsingPost = (data, query, params = {}) => this.request({
      path: `/customers/subaccount`,
      method: "POST",
      query,
      body: data,
      secure: true,
      type: "multipart/form-data" /* FormData */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name RegisterWillysPlusAccountUsingPost
     * @summary Register willys plus account
     * @request POST:/customers/willysplus
     * @secure
     */
    this.registerWillysPlusAccountUsingPost = (query, params = {}) => this.request({
      path: `/customers/willysplus`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
  }
};

// src/storefront/Axfood.ts
var Axfood = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Account
     * @name UpdateOfferStatusUsingPost
     * @summary Update offer status
     * @request POST:/axfood/rest/account/activateOffer
     * @secure
     */
    this.updateOfferStatusUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/activateOffer`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetBonusPointsHistoryUsingGet
     * @summary Get bonus points history
     * @request GET:/axfood/rest/account/bonusPointsHistory
     * @secure
     */
    this.getBonusPointsHistoryUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/account/bonusPointsHistory`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateCharityUsingPost
     * @summary Update charity
     * @request POST:/axfood/rest/account/charity
     * @secure
     */
    this.updateCharityUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/charity`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateCommunicationPreferencesUsingPost
     * @summary Update multiple communication options at once
     * @request POST:/axfood/rest/account/communicationPreferences
     * @secure
     */
    this.updateCommunicationPreferencesUsingPost = (data, params = {}) => this.request({
      path: `/axfood/rest/account/communicationPreferences`,
      method: "POST",
      body: data,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateSendDigitalReceiptUsingPost
     * @summary Update digital receipt
     * @request POST:/axfood/rest/account/digitalReceipt
     * @secure
     */
    this.updateSendDigitalReceiptUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/digitalReceipt`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateDigitalReceiptEmailCommChannelUsingPost
     * @summary Update digital receipt email comm channel
     * @request POST:/axfood/rest/account/digitalReceiptEmailCommChannel
     * @secure
     */
    this.updateDigitalReceiptEmailCommChannelUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/digitalReceiptEmailCommChannel`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateEmailUsingPost
     * @summary Update email
     * @request POST:/axfood/rest/account/email
     * @secure
     */
    this.updateEmailUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/email`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateEmailAndPhoneUsingPost
     * @summary Update email and phone number
     * @request POST:/axfood/rest/account/emailAndPhone
     * @secure
     */
    this.updateEmailAndPhoneUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/emailAndPhone`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name EmailAndPhoneAndDigitalReceiptUsingPost
     * @summary Update email and phone number And Digital Receipt
     * @request POST:/axfood/rest/account/emailAndPhoneAndDigitalReceipt
     * @secure
     */
    this.emailAndPhoneAndDigitalReceiptUsingPost = (updateEmailPhoneAndDigitalReceiptForm, params = {}) => this.request({
      path: `/axfood/rest/account/emailAndPhoneAndDigitalReceipt`,
      method: "POST",
      body: updateEmailPhoneAndDigitalReceiptForm,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateEmailNewsUsingPost
     * @summary Update email news
     * @request POST:/axfood/rest/account/emailNews
     * @secure
     */
    this.updateEmailNewsUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/emailNews`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name IsActivatedOfferInCartUsingPost
     * @summary Check if activated offer is in cart re-calc or not
     * @request POST:/axfood/rest/account/isActivatedOfferInCart
     * @secure
     */
    this.isActivatedOfferInCartUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/isActivatedOfferInCart`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateMonthlyCommunicationUsingPost
     * @summary Update monthly communication
     * @request POST:/axfood/rest/account/monthlyCommunication
     * @secure
     */
    this.updateMonthlyCommunicationUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/monthlyCommunication`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetOrderHistoryOfflineAndBonusUsingGet
     * @summary Get external order history and bonus for month
     * @request GET:/axfood/rest/account/orderBonus
     * @secure
     */
    this.getOrderHistoryOfflineAndBonusUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/account/orderBonus`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetOrderHistoryAndBonusUsingGet
     * @summary Get internal, external order history and bonus for month to date
     * @request GET:/axfood/rest/account/orderBonusCombined
     * @secure
     */
    this.getOrderHistoryAndBonusUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/account/orderBonusCombined`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetOrderHistoryUsingGet
     * @summary Get order history
     * @request GET:/axfood/rest/account/orders
     * @secure
     */
    this.getOrderHistoryUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/account/orders`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetPagedOrderHistoryAndBonusUsingGet
     * @summary Get paged internal, external order history and bonus for month to date
     * @request GET:/axfood/rest/account/pagedOrderBonusCombined
     * @secure
     */
    this.getPagedOrderHistoryAndBonusUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/account/pagedOrderBonusCombined`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdatePasswordUsingPost
     * @summary Update password
     * @request POST:/axfood/rest/account/password
     * @secure
     */
    this.updatePasswordUsingPost = (data, params = {}) => this.request({
      path: `/axfood/rest/account/password`,
      method: "POST",
      body: data,
      secure: true,
      type: "application/x-www-form-urlencoded" /* UrlEncoded */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateCellPhoneUsingPost
     * @summary Update phone
     * @request POST:/axfood/rest/account/phone
     * @secure
     */
    this.updateCellPhoneUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/phone`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetRecentCombinedOrderHistoryDatesUsingGet
     * @summary Get paged internal, external order history and bonus for month to date
     * @request GET:/axfood/rest/account/recentCombinedOrderHistoryDates
     * @secure
     */
    this.getRecentCombinedOrderHistoryDatesUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/account/recentCombinedOrderHistoryDates`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name RemoveLinkedCardUsingPost
     * @summary Remove connection to willys+ or Hemköp Club of a Card
     * @request POST:/axfood/rest/account/removeLinkedCard
     * @secure
     */
    this.removeLinkedCardUsingPost = (removeLinkedCardForm, params = {}) => this.request({
      path: `/axfood/rest/account/removeLinkedCard`,
      method: "POST",
      body: removeLinkedCardForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name ResetLatestLoginIsAutoLoginUsingPost
     * @summary Set latest login auto login false
     * @request POST:/axfood/rest/account/resetLatestLoginIsAutoLogin
     * @secure
     */
    this.resetLatestLoginIsAutoLoginUsingPost = (params = {}) => this.request({
      path: `/axfood/rest/account/resetLatestLoginIsAutoLogin`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name UpdateSmsNewsUsingPost
     * @summary Update sms news
     * @request POST:/axfood/rest/account/smsNews
     * @secure
     */
    this.updateSmsNewsUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/account/smsNews`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Account
     * @name GetAccountInfoUsingGet
     * @summary Get account info
     * @request GET:/axfood/rest/account/user-info
     * @secure
     */
    this.getAccountInfoUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/account/user-info`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Voucher
     * @name GetAllExternalVouchersOnlineAndOfflineUsingGet
     * @summary Get all external vouchers
     * @request GET:/axfood/rest/allexternalvouchers
     * @deprecated
     * @secure
     */
    this.getAllExternalVouchersOnlineAndOfflineUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/allexternalvouchers`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetArticlesUsingGet
     * @summary Get articles
     * @request GET:/axfood/rest/articles/{type}
     * @secure
     */
    this.getArticlesUsingGet = (type, query, params = {}) => this.request({
      path: `/axfood/rest/articles/${type}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name RegisterB2BCustomerUsingPost
     * @summary Register b2b customer
     * @request POST:/axfood/rest/b2bcustomers/register
     * @secure
     */
    this.registerB2BCustomerUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/b2bcustomers/register`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name LookupB2BCustomerUsingPost
     * @summary Lookup b2b customer
     * @request POST:/axfood/rest/b2bcustomers/search
     * @secure
     */
    this.lookupB2BCustomerUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/b2bcustomers/search`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetProductUsingGet
     * @summary Get offline product details
     * @request GET:/axfood/rest/c/{storeId}/{campaignCode}
     * @secure
     */
    this.getProductUsingGet = (storeId, campaignCode, params = {}) => this.request({
      path: `/axfood/rest/c/${storeId}/${campaignCode}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCartUsingGet
     * @summary Get session cart
     * @request GET:/axfood/rest/cart
     * @secure
     */
    this.getCartUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name ClearCartUsingDelete
     * @summary Cancel session cart
     * @request DELETE:/axfood/rest/cart
     * @secure
     */
    this.clearCartUsingDelete = (query, params = {}) => this.request({
      path: `/axfood/rest/cart`,
      method: "DELETE",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name PutToCartUsingPost
     * @summary Add product to cart
     * @request POST:/axfood/rest/cart/addProduct
     * @secure
     */
    this.putToCartUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/addProduct`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name AddToCartUsingPost
     * @summary Add products to cart
     * @request POST:/axfood/rest/cart/addProducts
     * @secure
     */
    this.addToCartUsingPost = (formListWrapper, query, params = {}) => this.request({
      path: `/axfood/rest/cart/addProducts`,
      method: "POST",
      query,
      body: formListWrapper,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name ContinueShopForOrderUsingPost
     * @summary Continue shop for order
     * @request POST:/axfood/rest/cart/carts
     * @secure
     */
    this.continueShopForOrderUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/carts`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCustomerAddressesUsingGet
     * @summary Get customer addresses
     * @request GET:/axfood/rest/cart/customer-addresses
     * @secure
     */
    this.getCustomerAddressesUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/customer-addresses`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCustomerContactUsingGet
     * @summary Get customer contact
     * @request GET:/axfood/rest/cart/customer-contact-info
     * @secure
     */
    this.getCustomerContactUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/customer-contact-info`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name SetCustomerContactUsingPost
     * @summary Set customer contact info
     * @request POST:/axfood/rest/cart/customer-contact-info
     * @secure
     */
    this.setCustomerContactUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/cart/customer-contact-info`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCustomerExtraInfoUsingGet
     * @summary Get customer extra info
     * @request GET:/axfood/rest/cart/customer-extra-info
     * @secure
     */
    this.getCustomerExtraInfoUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/customer-extra-info`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name SetCustomerExtraUsingPost
     * @summary Set customer extra door code, user comment and users order preference
     * @request POST:/axfood/rest/cart/customer-extra-info
     * @secure
     */
    this.setCustomerExtraUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/customer-extra-info`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCustomerLikelyDeliveryAddressUsingGet
     * @summary Get customer likely delivery address
     * @request GET:/axfood/rest/cart/customer-likely-delivery-address
     * @secure
     */
    this.getCustomerLikelyDeliveryAddressUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/customer-likely-delivery-address`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetDeliveryAddressUsingGet
     * @summary Get delivery address
     * @request GET:/axfood/rest/cart/delivery-address
     * @secure
     */
    this.getDeliveryAddressUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/delivery-address`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name SetDeliveryAddressUsingPost
     * @summary Set delivery address
     * @request POST:/axfood/rest/cart/delivery-address
     * @secure
     */
    this.setDeliveryAddressUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/delivery-address`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name SetDeliveryModeUsingPost
     * @summary Set delivery mode
     * @request POST:/axfood/rest/cart/delivery-mode/{deliveryModeCode}
     * @secure
     */
    this.setDeliveryModeUsingPost = (deliveryModeCode, query, params = {}) => this.request({
      path: `/axfood/rest/cart/delivery-mode/${deliveryModeCode}`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name CheckEccStockLevelsForSessionCartUsingGet
     * @summary ECC StockLevels
     * @request GET:/axfood/rest/cart/ecc/stocklevels
     * @secure
     */
    this.checkEccStockLevelsForSessionCartUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/ecc/stocklevels`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetMergablePersistentCartUsingGet
     * @summary Get mergeable persistent cart
     * @request GET:/axfood/rest/cart/merge
     * @secure
     */
    this.getMergablePersistentCartUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/merge`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name SetPostalCodeUsingPost
     * @summary Set postal code
     * @request POST:/axfood/rest/cart/postal-code
     * @secure
     */
    this.setPostalCodeUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/postal-code`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name UpdateDoNotReplaceFlagUsingPost
     * @summary Update do not replace flag for product
     * @request POST:/axfood/rest/cart/product/{productCode}/do-not-replace
     * @secure
     */
    this.updateDoNotReplaceFlagUsingPost = (productCode, query, params = {}) => this.request({
      path: `/axfood/rest/cart/product/${productCode}/do-not-replace`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name UpdateDoNotReplaceFlagUsingPost1
     * @summary Update do not replace flag for products on cart
     * @request POST:/axfood/rest/cart/products/do-not-replace
     * @secure
     */
    this.updateDoNotReplaceFlagUsingPost1 = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/products/do-not-replace`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name PutToCartUsingPost1
     * @summary Add order to cart
     * @request POST:/axfood/rest/cart/put/order/{order}
     * @secure
     */
    this.putToCartUsingPost1 = (order, params = {}) => this.request({
      path: `/axfood/rest/cart/put/order/${order}`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name RestoreCartUsingPost
     * @summary Restore cart
     * @request POST:/axfood/rest/cart/restore
     * @secure
     */
    this.restoreCartUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/restore`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name RestoreCartAndCloseOpenOrdersUsingPost
     * @summary Restore cart and close open orders
     * @request POST:/axfood/rest/cart/restore-v2
     * @secure
     */
    this.restoreCartAndCloseOpenOrdersUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/restore-v2`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name CheckCartStatusUsingGet
     * @summary Cart Status
     * @request GET:/axfood/rest/cart/status
     * @secure
     */
    this.checkCartStatusUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/status`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetConflictingEntriesUsingGet
     * @summary Status of conflicting cart entries
     * @request GET:/axfood/rest/cart/status/conflicts
     * @secure
     */
    this.getConflictingEntriesUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/status/conflicts`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetChangesOnRestoredCartFromLastSessionUsingGet
     * @summary Changes on restored cart from last session
     * @request GET:/axfood/rest/cart/status/lastsession
     * @secure
     */
    this.getChangesOnRestoredCartFromLastSessionUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/status/lastsession`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCartStatusForFreeDeliveryOrPickupUsingGet
     * @summary Threshold status for free delivery or pickup
     * @request GET:/axfood/rest/cart/status/threshold
     * @secure
     */
    this.getCartStatusForFreeDeliveryOrPickupUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/status/threshold`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreChangeAllowedUsingGet
     * @summary Is Change Store Allowed
     * @request GET:/axfood/rest/cart/store/change-allowed
     * @secure
     */
    this.getStoreChangeAllowedUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/cart/store/change-allowed`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name UpdateEkoReplacementFlagUsingPost
     * @summary Update eko replacement flag on cart entries
     * @request POST:/axfood/rest/cart/update-ekoreplacement
     * @secure
     */
    this.updateEkoReplacementFlagUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/update-ekoreplacement`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name UpdateNoReplacementFlagUsingPost
     * @summary (Depricated) Update no replacement flag on cart entries
     * @request POST:/axfood/rest/cart/update-noreplacement
     * @secure
     */
    this.updateNoReplacementFlagUsingPost = (params = {}) => this.request({
      path: `/axfood/rest/cart/update-noreplacement`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name UpdateCartModificationsUsingPost
     * @summary Update cart modifications
     * @request POST:/axfood/rest/cart/updatecartmodifications
     * @secure
     */
    this.updateCartModificationsUsingPost = (params = {}) => this.request({
      path: `/axfood/rest/cart/updatecartmodifications`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name ApplyVoucherToCartUsingPost
     * @summary Apply voucher to cart
     * @request POST:/axfood/rest/cart/voucher
     * @secure
     */
    this.applyVoucherToCartUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/cart/voucher`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name ReleaseVoucherFromCartUsingDelete
     * @summary Release voucher from cart
     * @request DELETE:/axfood/rest/cart/voucher/{voucherCode}
     * @secure
     */
    this.releaseVoucherFromCartUsingDelete = (voucherCode, params = {}) => this.request({
      path: `/axfood/rest/cart/voucher/${voucherCode}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name InitializeCheckoutUsingGet
     * @summary Initialize checkout
     * @request GET:/axfood/rest/checkout
     * @secure
     */
    this.initializeCheckoutUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/checkout`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name BankIdAuthUsingPost
     * @summary Authorize with bankId
     * @request POST:/axfood/rest/checkout/bankid/auth
     * @secure
     */
    this.bankIdAuthUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/checkout/bankid/auth`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name BankCancelUsingPost
     * @summary Cancel bankId sign
     * @request POST:/axfood/rest/checkout/bankid/cancel
     * @secure
     */
    this.bankCancelUsingPost = (orderRef, params = {}) => this.request({
      path: `/axfood/rest/checkout/bankid/cancel`,
      method: "POST",
      body: orderRef,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name BankIdCollectUsingPost
     * @summary Collect with bankId
     * @request POST:/axfood/rest/checkout/bankid/collect
     * @secure
     */
    this.bankIdCollectUsingPost = (orderRef, params = {}) => this.request({
      path: `/axfood/rest/checkout/bankid/collect`,
      method: "POST",
      body: orderRef,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name BankIdCollectLoginUsingPost
     * @summary Collect with bankId login
     * @request POST:/axfood/rest/checkout/bankid/collect-login
     * @secure
     */
    this.bankIdCollectLoginUsingPost = (orderRef, params = {}) => this.request({
      path: `/axfood/rest/checkout/bankid/collect-login`,
      method: "POST",
      body: orderRef,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name BankIdQrDataUsingPost
     * @summary Refresh qr data
     * @request POST:/axfood/rest/checkout/bankid/qr
     * @secure
     */
    this.bankIdQrDataUsingPost = (params = {}) => this.request({
      path: `/axfood/rest/checkout/bankid/qr`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name BankIdSignUsingPost
     * @summary Sign with bankId
     * @request POST:/axfood/rest/checkout/bankid/sign
     * @secure
     */
    this.bankIdSignUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/checkout/bankid/sign`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name SetPaymentModeUsingPost
     * @summary Set payment mode for user
     * @request POST:/axfood/rest/checkout/paymentmode
     * @secure
     */
    this.setPaymentModeUsingPost = (paymentModeJson, params = {}) => this.request({
      path: `/axfood/rest/checkout/paymentmode`,
      method: "POST",
      body: paymentModeJson,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name GetValidPaymentModesForUserUsingGet
     * @summary Get payment modes for user
     * @request GET:/axfood/rest/checkout/paymentmodes
     * @secure
     */
    this.getValidPaymentModesForUserUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/checkout/paymentmodes`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Session
     * @name GetCsrfTokenUsingGet
     * @summary Get a csrf token for session
     * @request GET:/axfood/rest/csrf-token
     * @secure
     */
    this.getCsrfTokenUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/csrf-token`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name GetSessionInformationUsingGet
     * @summary Get session information for customer
     * @request GET:/axfood/rest/customer
     * @secure
     */
    this.getSessionInformationUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer service
     * @name GetAllReasonsUsingGet
     * @summary Get all reasons
     * @request GET:/axfood/rest/customer-service/reasons
     * @secure
     */
    this.getAllReasonsUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer-service/reasons`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name AppReactEligibleUsingGet
     * @summary App react eligible
     * @request GET:/axfood/rest/customer/app-react-eligible
     * @secure
     */
    this.appReactEligibleUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer/app-react-eligible`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name GetBonusBenefitInfoUsingGet
     * @summary Get bonus benefit info
     * @request GET:/axfood/rest/customer/bonusBenefitInfo
     * @secure
     */
    this.getBonusBenefitInfoUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer/bonusBenefitInfo`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name NewHeaderFooterRedesignEnabledUsingGet
     * @summary Customer header footer design enabled
     * @request GET:/axfood/rest/customer/customer-headerfooterredesign-enabled
     * @secure
     */
    this.newHeaderFooterRedesignEnabledUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer/customer-headerfooterredesign-enabled`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name NewCustomerProgramEnabledUsingGet
     * @summary Customer new program enabled
     * @request GET:/axfood/rest/customer/customer-newprogram-enabled
     * @secure
     */
    this.newCustomerProgramEnabledUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer/customer-newprogram-enabled`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name GenerateDataMatrixCodeForCustomerSsnUsingGet
     * @summary Data matrix code for customer ssn
     * @request GET:/axfood/rest/customer/data-matrix-code
     * @secure
     */
    this.generateDataMatrixCodeForCustomerSsnUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/customer/data-matrix-code`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name DeleteAgreementUsingDelete
     * @summary Delete agreement
     * @request DELETE:/axfood/rest/customer/savedcard/{agreementId}
     * @secure
     */
    this.deleteAgreementUsingDelete = (agreementId, params = {}) => this.request({
      path: `/axfood/rest/customer/savedcard/${agreementId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name ScanAndGoEligibleUsingGet
     * @summary Scan an go eligible
     * @request GET:/axfood/rest/customer/scan-and-go-eligible
     * @secure
     */
    this.scanAndGoEligibleUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer/scan-and-go-eligible`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name GetSustainablesUsingGet
     * @summary Get sustainables
     * @request GET:/axfood/rest/customer/sustainables
     * @secure
     */
    this.getSustainablesUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/customer/sustainables`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name IsDeliveryAvailableForAddressLineUsingGet
     * @summary Is delivery available for address line
     * @request GET:/axfood/rest/delivery/addressline
     * @secure
     */
    this.isDeliveryAvailableForAddressLineUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/delivery/addressline`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name IsDeliveryAvailableUsingGet
     * @summary Is delivery available
     * @request GET:/axfood/rest/delivery/delivery-available
     * @secure
     */
    this.isDeliveryAvailableUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/delivery/delivery-available`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name RegisterDeviceUsingGet
     * @summary Register device
     * @request GET:/axfood/rest/device/register
     * @secure
     */
    this.registerDeviceUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/device/register`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Voucher
     * @name GetExternalVoucherUsingGet
     * @summary Get external vouchers
     * @request GET:/axfood/rest/externalvoucher
     * @secure
     */
    this.getExternalVoucherUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/externalvoucher`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Voucher
     * @name ActivateExternalVoucherUsingPut
     * @summary Activate external voucher
     * @request PUT:/axfood/rest/externalvoucher/activate/{voucherCode}
     * @secure
     */
    this.activateExternalVoucherUsingPut = (voucherCode, params = {}) => this.request({
      path: `/axfood/rest/externalvoucher/activate/${voucherCode}`,
      method: "PUT",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Voucher
     * @name GetExternalVoucherForClubInfoUsingGet
     * @summary Get external voucher for club info
     * @request GET:/axfood/rest/externalvoucher/clubinfo
     * @secure
     */
    this.getExternalVoucherForClubInfoUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/externalvoucher/clubinfo`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Voucher
     * @name GetExternalVoucherCountUsingGet
     * @summary Get all external vouchers
     * @request GET:/axfood/rest/externalvoucher/count
     * @secure
     */
    this.getExternalVoucherCountUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/externalvoucher/count`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Voucher
     * @name GetInvalidExternalVoucherUsingGet
     * @summary Get invalid external voucher
     * @request GET:/axfood/rest/externalvoucher/invalid/{orderId}
     * @secure
     */
    this.getInvalidExternalVoucherUsingGet = (orderId, params = {}) => this.request({
      path: `/axfood/rest/externalvoucher/invalid/${orderId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetEntriesForCategoryUsingGet
     * @summary Get entries for category
     * @request GET:/axfood/rest/faq/category/{categoryUid}/entries
     * @secure
     */
    this.getEntriesForCategoryUsingGet = (categoryUid, params = {}) => this.request({
      path: `/axfood/rest/faq/category/${categoryUid}/entries`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Feature
     * @name FeaturesUsingGet
     * @summary Get features
     * @request GET:/axfood/rest/feature
     * @secure
     */
    this.featuresUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/feature`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Feature
     * @name GetFeaturesForServiceUsingGet
     * @summary Get features for a service
     * @request GET:/axfood/rest/feature/service/{serviceName}
     * @secure
     */
    this.getFeaturesForServiceUsingGet = (serviceName, params = {}) => this.request({
      path: `/axfood/rest/feature/service/${serviceName}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Feature
     * @name GetFeatureToggleUsingGet
     * @summary Get feature toggle
     * @request GET:/axfood/rest/feature/{featureName}
     * @secure
     */
    this.getFeatureToggleUsingGet = (featureName, params = {}) => this.request({
      path: `/axfood/rest/feature/${featureName}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetFullUserWishListsUsingGet
     * @summary Get full user wish lists
     * @request GET:/axfood/rest/full-user-wishlist
     * @secure
     */
    this.getFullUserWishListsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/full-user-wishlist`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetJobAdvertsUsingGet
     * @summary Get job adverts
     * @request GET:/axfood/rest/jobs
     * @secure
     */
    this.getJobAdvertsUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/jobs`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name SetKlarnaAuthenticationTokenUsingPost
     * @summary Set klarna authentication token
     * @request POST:/axfood/rest/klarna/authCallbackEndpoint
     * @secure
     */
    this.setKlarnaAuthenticationTokenUsingPost = (klarnaCallbackBody, query, params = {}) => this.request({
      path: `/axfood/rest/klarna/authCallbackEndpoint`,
      method: "POST",
      query,
      body: klarnaCallbackBody,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CancelOrderUsingPost1
     * @summary Cancel klarna order
     * @request POST:/axfood/rest/klarna/cancel-order
     * @secure
     */
    this.cancelOrderUsingPost1 = (query, params = {}) => this.request({
      path: `/axfood/rest/klarna/cancel-order`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CleanKlarnaSessionUsingGet
     * @summary Clean klarna session
     * @request GET:/axfood/rest/klarna/clean-session
     * @secure
     */
    this.cleanKlarnaSessionUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/klarna/clean-session`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name CreateSessionUsingGet
     * @summary Create klarna payment session
     * @request GET:/axfood/rest/klarna/payment-session
     * @secure
     */
    this.createSessionUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/klarna/payment-session`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Checkout
     * @name SetPaymentMethodUsingPost
     * @summary Set klarna payment method
     * @request POST:/axfood/rest/klarna/set-klarna-paymentmethod
     * @secure
     */
    this.setPaymentMethodUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/klarna/set-klarna-paymentmethod`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name VerifyEmailUsingPost
     * @summary Verify email
     * @request POST:/axfood/rest/lost-password/verify-email
     * @secure
     */
    this.verifyEmailUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/lost-password/verify-email`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name VerifyMcnUsingPost
     * @summary Verify membership number
     * @request POST:/axfood/rest/lost-password/verify-membership-number
     * @secure
     */
    this.verifyMcnUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/lost-password/verify-membership-number`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name VerifyPhoneUsingPost
     * @summary Verify phone
     * @request POST:/axfood/rest/lost-password/verify-phone
     * @secure
     */
    this.verifyPhoneUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/lost-password/verify-phone`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name GetCartUsingGet1
     * @summary Get session cart
     * @request GET:/axfood/rest/minicart
     * @secure
     */
    this.getCartUsingGet1 = (params = {}) => this.request({
      path: `/axfood/rest/minicart`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name ClearCartUsingDelete1
     * @summary Cancel session cart
     * @request DELETE:/axfood/rest/minicart
     * @secure
     */
    this.clearCartUsingDelete1 = (query, params = {}) => this.request({
      path: `/axfood/rest/minicart`,
      method: "DELETE",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name PutToCartUsingPost2
     * @summary Add product to cart
     * @request POST:/axfood/rest/minicart/addProduct
     * @secure
     */
    this.putToCartUsingPost2 = (query, params = {}) => this.request({
      path: `/axfood/rest/minicart/addProduct`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name AddToCartUsingPost1
     * @summary Add products to cart
     * @request POST:/axfood/rest/minicart/addProducts
     * @secure
     */
    this.addToCartUsingPost1 = (formListWrapper, query, params = {}) => this.request({
      path: `/axfood/rest/minicart/addProducts`,
      method: "POST",
      query,
      body: formListWrapper,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Cart
     * @name PutToCartUsingPost3
     * @summary Add order to cart
     * @request POST:/axfood/rest/minicart/put/order/{order}
     * @secure
     */
    this.putToCartUsingPost3 = (order, params = {}) => this.request({
      path: `/axfood/rest/minicart/put/order/${order}`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetItemsForYearUsingGet
     * @summary Get items for year
     * @request GET:/axfood/rest/news/year/{yearUid}/items
     * @secure
     */
    this.getItemsForYearUsingGet = (yearUid, params = {}) => this.request({
      path: `/axfood/rest/news/year/${yearUid}/items`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetAppVersionNonStaticUsingGet
     * @summary Get supported app version from db
     * @request GET:/axfood/rest/non-static-supported-app-version
     * @secure
     */
    this.getAppVersionNonStaticUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/non-static-supported-app-version`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name CancelOrderUsingPost
     * @summary Cancel order
     * @request POST:/axfood/rest/order/cancelOrder/{orderCode}
     * @secure
     */
    this.cancelOrderUsingPost = (orderCode, params = {}) => this.request({
      path: `/axfood/rest/order/cancelOrder/${orderCode}`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name LockOrderBoxUsingPost
     * @summary Locks a box of a click and collect order
     * @request POST:/axfood/rest/order/clickandcollect/{orderCode}/boxes/{boxId}/lock
     * @secure
     */
    this.lockOrderBoxUsingPost = (orderCode, boxId, params = {}) => this.request({
      path: `/axfood/rest/order/clickandcollect/${orderCode}/boxes/${boxId}/lock`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name UnlockOrderBoxUsingPost
     * @summary Unlocks a box of a click and collect order
     * @request POST:/axfood/rest/order/clickandcollect/{orderCode}/boxes/{boxId}/unlock
     * @secure
     */
    this.unlockOrderBoxUsingPost = (orderCode, boxId, query, params = {}) => this.request({
      path: `/axfood/rest/order/clickandcollect/${orderCode}/boxes/${boxId}/unlock`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name FinishCollectProcessUsingPost
     * @summary Finishes the collect process of an order
     * @request POST:/axfood/rest/order/clickandcollect/{orderCode}/finish
     * @secure
     */
    this.finishCollectProcessUsingPost = (orderCode, params = {}) => this.request({
      path: `/axfood/rest/order/clickandcollect/${orderCode}/finish`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name StartCollectProcessUsingPost
     * @summary Starts the collect process of an order
     * @request POST:/axfood/rest/order/clickandcollect/{orderCode}/start
     * @secure
     */
    this.startCollectProcessUsingPost = (orderCode, params = {}) => this.request({
      path: `/axfood/rest/order/clickandcollect/${orderCode}/start`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name IsOrderProcessFinishedUsingGet
     * @summary Is order process finished
     * @request GET:/axfood/rest/order/isOrderProccessFinished/{orderCode}
     * @secure
     */
    this.isOrderProcessFinishedUsingGet = (orderCode, params = {}) => this.request({
      path: `/axfood/rest/order/isOrderProccessFinished/${orderCode}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetLastOrderLinesUsingGet
     * @summary Get last order lines
     * @request GET:/axfood/rest/order/last/lines
     * @secure
     */
    this.getLastOrderLinesUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/order/last/lines`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetOrderHistoryUsingGet1
     * @summary Get order history
     * @request GET:/axfood/rest/order/orderHistory
     * @secure
     */
    this.getOrderHistoryUsingGet1 = (params = {}) => this.request({
      path: `/axfood/rest/order/orderHistory`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetOrdersUsingGet
     * @summary Get orders for complaint
     * @request GET:/axfood/rest/order/orders/complaint
     * @secure
     */
    this.getOrdersUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/order/orders/complaint`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetDigitalReceiptUsingGet
     * @summary Is digital receipt
     * @request GET:/axfood/rest/order/orders/digitalreceipt/{getDigitalReceiptReference}
     * @secure
     */
    this.getDigitalReceiptUsingGet = (getDigitalReceiptReference, query, params = {}) => this.request({
      path: `/axfood/rest/order/orders/digitalreceipt/${getDigitalReceiptReference}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetOrderUsingGet
     * @summary Get order
     * @request GET:/axfood/rest/order/orders/{orderCode}
     * @secure
     */
    this.getOrderUsingGet = (orderCode, params = {}) => this.request({
      path: `/axfood/rest/order/orders/${orderCode}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetOrderReceiptUsingGet
     * @summary Get order receipt
     * @request GET:/axfood/rest/order/orders/{orderCode}/receipt
     * @secure
     */
    this.getOrderReceiptUsingGet = (orderCode, params = {}) => this.request({
      path: `/axfood/rest/order/orders/${orderCode}/receipt`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name ValidateOrderEntriesUsingGet
     * @summary Validate order entries
     * @request GET:/axfood/rest/order/validate/{orderCode}
     * @secure
     */
    this.validateOrderEntriesUsingGet = (orderCode, query, params = {}) => this.request({
      path: `/axfood/rest/order/validate/${orderCode}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Delivery
     * @name GetOngoingTrackingStatusesUsingGet
     * @summary Get order tracking status for ongoing orders of customer
     * @request GET:/axfood/rest/orderDeliveryStatus/ongoing-tracking-statuses
     * @secure
     */
    this.getOngoingTrackingStatusesUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/orderDeliveryStatus/ongoing-tracking-statuses`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Delivery
     * @name GetOrderTrackingStatusUsingGet
     * @summary Get order tracking status
     * @request GET:/axfood/rest/orderDeliveryStatus/{trackingID}
     * @secure
     */
    this.getOrderTrackingStatusUsingGet = (trackingId, params = {}) => this.request({
      path: `/axfood/rest/orderDeliveryStatus/${trackingId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetFullOrderDataUsingGet
     * @summary Get data for an order
     * @request GET:/axfood/rest/orderdata
     * @secure
     */
    this.getFullOrderDataUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/orderdata`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Order
     * @name GetFullOrderDataUsingGet1
     * @summary Get data for an order
     * @request GET:/axfood/rest/orderdata/
     * @secure
     */
    this.getFullOrderDataUsingGet1 = (query, params = {}) => this.request({
      path: `/axfood/rest/orderdata/`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetProductUsingGet1
     * @summary Get product details
     * @request GET:/axfood/rest/p/{productCode}
     * @secure
     */
    this.getProductUsingGet1 = (productCode, query, params = {}) => this.request({
      path: `/axfood/rest/p/${productCode}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetProductV2UsingGet
     * @summary Get product detail in Sections
     * @request GET:/axfood/rest/p/{productCode}/section/{sectionName}
     * @secure
     */
    this.getProductV2UsingGet = (productCode, sectionName, params = {}) => this.request({
      path: `/axfood/rest/p/${productCode}/section/${sectionName}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Personal Elements
     * @name GetPersonalElementUsingGet
     * @summary Get personal element
     * @request GET:/axfood/rest/personalElement
     * @secure
     */
    this.getPersonalElementUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/personalElement`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Personal Elements
     * @name GetListOfPersonalElementsUsingGet
     * @summary Get list of personal elements
     * @request GET:/axfood/rest/personalElementList
     * @secure
     */
    this.getListOfPersonalElementsUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/personalElementList`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Session
     * @name PingUsingGet
     * @summary ping
     * @request GET:/axfood/rest/ping
     * @secure
     */
    this.pingUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/ping`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetProductRecommendationUsingGet
     * @summary Get product recommendations
     * @request GET:/axfood/rest/products/{productCode}/{displayType}
     * @secure
     */
    this.getProductRecommendationUsingGet = (productCode, displayType, query, params = {}) => this.request({
      path: `/axfood/rest/products/${productCode}/${displayType}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetMixPromotionsUsingGet
     * @summary Get mix promotions
     * @request GET:/axfood/rest/promotionproduct/mix
     * @secure
     */
    this.getMixPromotionsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/promotionproduct/mix`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetOfflinePromotionsUsingGet
     * @summary Get offline promotions
     * @request GET:/axfood/rest/promotionproduct/offline
     * @secure
     */
    this.getOfflinePromotionsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/promotionproduct/offline`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetOnlinePromotionsUsingGet
     * @summary Get online promotions
     * @request GET:/axfood/rest/promotionproduct/online
     * @secure
     */
    this.getOnlinePromotionsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/promotionproduct/online`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetProductsFromPromotionUsingGet
     * @summary Get products for given promotion code and store excluding products in parameter
     * @request GET:/axfood/rest/promotionproduct/{promotionCode}/products
     * @secure
     */
    this.getProductsFromPromotionUsingGet = (promotionCode, query, params = {}) => this.request({
      path: `/axfood/rest/promotionproduct/${promotionCode}/products`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetPromotionProductsUsingGet
     * @summary Get promotion products
     * @request GET:/axfood/rest/promotions/{promotionCode}/products
     * @secure
     */
    this.getPromotionProductsUsingGet = (promotionCode, query, params = {}) => this.request({
      path: `/axfood/rest/promotions/${promotionCode}/products`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetRecipeWishlistUsingGet1
     * @summary Get recipe wish list
     * @request GET:/axfood/rest/recipe-wishlist/{id}
     * @secure
     */
    this.getRecipeWishlistUsingGet1 = (id, params = {}) => this.request({
      path: `/axfood/rest/recipe-wishlist/${id}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name AddEntryToRecipeCartUsingPost
     * @summary Add entry to recipe cart
     * @request POST:/axfood/rest/recipe/addEntryToRecipeCart
     * @secure
     */
    this.addEntryToRecipeCartUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/recipe/addEntryToRecipeCart`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name AddReplacementProductUsingPost
     * @summary Add replacement products
     * @request POST:/axfood/rest/recipe/addReplacementProducts
     * @secure
     */
    this.addReplacementProductUsingPost = (body, params = {}) => this.request({
      path: `/axfood/rest/recipe/addReplacementProducts`,
      method: "POST",
      body,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name AddRecipeToCartUsingPost
     * @summary Add recipe to cart
     * @request POST:/axfood/rest/recipe/addToCart
     * @secure
     */
    this.addRecipeToCartUsingPost = (body, params = {}) => this.request({
      path: `/axfood/rest/recipe/addToCart`,
      method: "POST",
      body,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name ChangePortionsUsingPatch
     * @summary Change portions to recipe
     * @request PATCH:/axfood/rest/recipe/changePortions
     * @secure
     */
    this.changePortionsUsingPatch = (request, params = {}) => this.request({
      path: `/axfood/rest/recipe/changePortions`,
      method: "PATCH",
      body: request,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name AddFavoriteUsingPost
     * @summary Mark recipe as favorite
     * @request POST:/axfood/rest/recipe/favorite
     * @secure
     */
    this.addFavoriteUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/recipe/favorite`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name RemoveFavoriteUsingDelete
     * @summary Unmark recipe as favorite
     * @request DELETE:/axfood/rest/recipe/favorite
     * @secure
     */
    this.removeFavoriteUsingDelete = (query, params = {}) => this.request({
      path: `/axfood/rest/recipe/favorite`,
      method: "DELETE",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name FindRecipeReplacementProductsUsingGet
     * @summary Find recipe replacement products
     * @request GET:/axfood/rest/recipe/findRecipeReplacementProducts/{recipeId}
     * @secure
     */
    this.findRecipeReplacementProductsUsingGet = (recipeId, query, params = {}) => this.request({
      path: `/axfood/rest/recipe/findRecipeReplacementProducts/${recipeId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name GetFavoriteRecipesUsingGet
     * @summary Gets customers favorite recipes
     * @request GET:/axfood/rest/recipe/getFavorites
     * @secure
     */
    this.getFavoriteRecipesUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/recipe/getFavorites`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name GetRelatedRecipesUsingGet
     * @summary Get related recipes
     * @request GET:/axfood/rest/recipe/getRelatedRecipes/{recipeId}
     * @secure
     */
    this.getRelatedRecipesUsingGet = (recipeId, params = {}) => this.request({
      path: `/axfood/rest/recipe/getRelatedRecipes/${recipeId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name GetRecipeUsingGet1
     * @summary Get recipe
     * @request GET:/axfood/rest/recipe/preview/{recipeId}
     * @secure
     */
    this.getRecipeUsingGet1 = (recipeId, params = {}) => this.request({
      path: `/axfood/rest/recipe/preview/${recipeId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name RemoveEntryFromRecipeCartUsingPost
     * @summary Remove entry from recipe cart
     * @request POST:/axfood/rest/recipe/removeEntryFromRecipeCart
     * @secure
     */
    this.removeEntryFromRecipeCartUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/recipe/removeEntryFromRecipeCart`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name RemoveRecipeFromCartUsingPost
     * @summary Remove recipe from cart
     * @request POST:/axfood/rest/recipe/removeFromCart
     * @secure
     */
    this.removeRecipeFromCartUsingPost = (body, params = {}) => this.request({
      path: `/axfood/rest/recipe/removeFromCart`,
      method: "POST",
      body,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name GetRecipeUsingGet
     * @summary Get recipe
     * @request GET:/axfood/rest/recipe/{recipeId}
     * @secure
     */
    this.getRecipeUsingGet = (recipeId, params = {}) => this.request({
      path: `/axfood/rest/recipe/${recipeId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name CompleteCustomerInfoUsingPost
     * @summary Register complete customer info
     * @request POST:/axfood/rest/register/complete-customer-info
     * @secure
     */
    this.completeCustomerInfoUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/register/complete-customer-info`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name CompleteMembershipUsingPost
     * @summary Register complete membership
     * @request POST:/axfood/rest/register/complete-membership
     * @secure
     */
    this.completeMembershipUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/register/complete-membership`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name VerifyEmailUsingPost1
     * @summary Verify email
     * @request POST:/axfood/rest/register/verify-email
     * @secure
     */
    this.verifyEmailUsingPost1 = (form, params = {}) => this.request({
      path: `/axfood/rest/register/verify-email`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name VerifyIdInSourceSystemUsingPost
     * @summary Verify id in source system
     * @request POST:/axfood/rest/register/verify-membership-number
     * @secure
     */
    this.verifyIdInSourceSystemUsingPost = (form, params = {}) => this.request({
      path: `/axfood/rest/register/verify-membership-number`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Customer
     * @name VerifyPhoneUsingPost1
     * @summary Verify phone
     * @request POST:/axfood/rest/register/verify-phone
     * @secure
     */
    this.verifyPhoneUsingPost1 = (form, params = {}) => this.request({
      path: `/axfood/rest/register/verify-phone`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreUsingGet
     * @summary Searches for stores
     * @request GET:/axfood/rest/search/store
     * @secure
     */
    this.getStoreUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/search/store`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Shared wishlist
     * @name GetSharedWishlistUsingGet
     * @summary Get shared wishlist by ID
     * @request GET:/axfood/rest/shared-wishlist/{wishlistId}
     * @secure
     */
    this.getSharedWishlistUsingGet = (wishlistId, params = {}) => this.request({
      path: `/axfood/rest/shared-wishlist/${wishlistId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Shared wishlist
     * @name ShareWishlistUsingPost
     * @summary Make the given user wishlist shareable
     * @request POST:/axfood/rest/shared-wishlist/{wishlistId}/share
     * @secure
     */
    this.shareWishlistUsingPost = (wishlistId, params = {}) => this.request({
      path: `/axfood/rest/shared-wishlist/${wishlistId}/share`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Delivery
     * @name GetDeliverabilityUsingGet
     * @summary getDeliverability
     * @request GET:/axfood/rest/shipping/delivery/{postalCode}/deliverability
     * @secure
     */
    this.getDeliverabilityUsingGet = (postalCode, query, params = {}) => this.request({
      path: `/axfood/rest/shipping/delivery/${postalCode}/deliverability`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetWishListsUsingGet
     * @summary Get site wish lists
     * @request GET:/axfood/rest/site-wishlist
     * @secure
     */
    this.getWishListsUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/site-wishlist`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetSiteWishlistUsingGet
     * @summary Get site wish list
     * @request GET:/axfood/rest/site-wishlist/{id}
     * @secure
     */
    this.getSiteWishlistUsingGet = (id, params = {}) => this.request({
      path: `/axfood/rest/site-wishlist/${id}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name IsCurrentSlotAvailableForPostalCodeUsingGet
     * @summary Is current slot available for postal code
     * @request GET:/axfood/rest/slot/currentSlotAvailableForPostalCode
     * @secure
     */
    this.isCurrentSlotAvailableForPostalCodeUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/slot/currentSlotAvailableForPostalCode`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name GetHomeDeliverySlotsUsingGet
     * @summary Get home delivery slots
     * @request GET:/axfood/rest/slot/homeDelivery
     * @secure
     */
    this.getHomeDeliverySlotsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/slot/homeDelivery`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name GetHomeDeliverySlotsForDayUsingGet
     * @summary Get home delivery slots for day
     * @request GET:/axfood/rest/slot/homeDelivery/daysAvailability
     * @secure
     */
    this.getHomeDeliverySlotsForDayUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/slot/homeDelivery/daysAvailability`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name GetPickInStoreSlotsUsingGet
     * @summary Get pick in store slots
     * @request GET:/axfood/rest/slot/pickInStore
     * @secure
     */
    this.getPickInStoreSlotsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/slot/pickInStore`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name GetPickInStoreSlotsForDayUsingGet
     * @summary Get pick in store slots for day
     * @request GET:/axfood/rest/slot/pickInStore/daysAvailability
     * @secure
     */
    this.getPickInStoreSlotsForDayUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/slot/pickInStore/daysAvailability`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name GetSlotSetToCartUsingGet
     * @summary Get slot in cart
     * @request GET:/axfood/rest/slot/slotInCart
     * @secure
     */
    this.getSlotSetToCartUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/slot/slotInCart`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name RemoveSlotFromCartUsingDelete
     * @summary Remove slot from cart
     * @request DELETE:/axfood/rest/slot/slotInCart
     * @secure
     */
    this.removeSlotFromCartUsingDelete = (params = {}) => this.request({
      path: `/axfood/rest/slot/slotInCart`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Slot
     * @name SetSlotToCartUsingPost
     * @summary Set slot to cart
     * @request POST:/axfood/rest/slot/slotInCart/{slotCode}
     * @secure
     */
    this.setSlotToCartUsingPost = (slotCode, tmsDeliveryWindowJson, query, params = {}) => this.request({
      path: `/axfood/rest/slot/slotInCart/${slotCode}`,
      method: "POST",
      query,
      body: tmsDeliveryWindowJson,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetSmartSuggestionComponentsUsingGet
     * @summary Get smart suggestion promotions
     * @request GET:/axfood/rest/smart-suggestions/{storeId}/promotion-component/{promotionComponentUID}
     * @secure
     */
    this.getSmartSuggestionComponentsUsingGet = (storeId, promotionComponentUid, query, params = {}) => this.request({
      path: `/axfood/rest/smart-suggestions/${storeId}/promotion-component/${promotionComponentUid}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoresUsingGet
     * @summary Get available stores
     * @request GET:/axfood/rest/store
     * @secure
     */
    this.getStoresUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/store`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name ActivateStoreInSessionUsingPost
     * @summary Set active store in session
     * @request POST:/axfood/rest/store/activate
     * @secure
     */
    this.activateStoreInSessionUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/store/activate`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetCurrentBaseStoreUsingGet
     * @summary Get current base store
     * @request GET:/axfood/rest/store/active
     * @secure
     */
    this.getCurrentBaseStoreUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/store/active`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetClickAndCollectStoresUsingGet
     * @summary Get click and collect stores
     * @request GET:/axfood/rest/store/clickcollect
     * @secure
     */
    this.getClickAndCollectStoresUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/store/clickcollect`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name CheckIfStoreIsNotPermanentlyClosedUsingGet
     * @summary Is store permanently closed
     * @request GET:/axfood/rest/store/isPermanentlyClosed
     * @secure
     */
    this.checkIfStoreIsNotPermanentlyClosedUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/store/isPermanentlyClosed`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetSuggestedPickUpStoreUsingGet
     * @summary Get customer suggested pickup store
     * @request GET:/axfood/rest/store/suggested-pickup-store
     * @secure
     */
    this.getSuggestedPickUpStoreUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/store/suggested-pickup-store`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingGet1
     * @summary Get store information
     * @request GET:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingGet1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingHead1
     * @summary Get store information
     * @request HEAD:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingHead1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "HEAD",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingPost1
     * @summary Get store information
     * @request POST:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingPost1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "POST",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingPut1
     * @summary Get store information
     * @request PUT:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingPut1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "PUT",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingDelete1
     * @summary Get store information
     * @request DELETE:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingDelete1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingOptions1
     * @summary Get store information
     * @request OPTIONS:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingOptions1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "OPTIONS",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetStoreInformationUsingPatch1
     * @summary Get store information
     * @request PATCH:/axfood/rest/store/{storeId}
     * @secure
     */
    this.getStoreInformationUsingPatch1 = (storeId, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}`,
      method: "PATCH",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name CheckIfPointIsCloseToStoreUsingGet
     * @summary Is the given geopoint close to the given store
     * @request GET:/axfood/rest/store/{storeId}/pointClose
     * @secure
     */
    this.checkIfPointIsCloseToStoreUsingGet = (storeId, query, params = {}) => this.request({
      path: `/axfood/rest/store/${storeId}/pointClose`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Store
     * @name GetCurrentStoreDetailsUsingGet
     * @summary Get current store details
     * @request GET:/axfood/rest/storeDetails
     * @secure
     */
    this.getCurrentStoreDetailsUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/storeDetails`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name GetAddressSuggestionsUsingGet
     * @summary Get address suggestions for addressline
     * @request GET:/axfood/rest/suggest/addressline
     * @secure
     */
    this.getAddressSuggestionsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/suggest/addressline`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name GetLocalitySuggestionsUsingGet
     * @summary Get locality suggestions
     * @request GET:/axfood/rest/suggest/locality
     * @secure
     */
    this.getLocalitySuggestionsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/suggest/locality`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name GetPostalCodeSuggestionsUsingGet
     * @summary Get postal code suggestions
     * @request GET:/axfood/rest/suggest/postalcode
     * @secure
     */
    this.getPostalCodeSuggestionsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/suggest/postalcode`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name GetAddressSuggestionsUsingGet1
     * @summary Get address suggestions
     * @request GET:/axfood/rest/suggest/street
     * @secure
     */
    this.getAddressSuggestionsUsingGet1 = (query, params = {}) => this.request({
      path: `/axfood/rest/suggest/street`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetAppVersionUsingGet
     * @summary Get supported app version with static value from nginx
     * @request GET:/axfood/rest/supported-app-version
     * @secure
     */
    this.getAppVersionUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/supported-app-version`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetTranslationsUsingGet
     * @summary Get translations
     * @request GET:/axfood/rest/translation
     * @secure
     */
    this.getTranslationsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/translation`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetReactTranslationsUsingGet
     * @summary Get react translations
     * @request GET:/axfood/rest/translation/react
     * @secure
     */
    this.getReactTranslationsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/translation/react`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Site
     * @name GetReactTranslationsForNamespaceUsingGet
     * @summary Get react translations for namespace
     * @request GET:/axfood/rest/translation/react/{namespace}
     * @secure
     */
    this.getReactTranslationsForNamespaceUsingGet = (namespace, query, params = {}) => this.request({
      path: `/axfood/rest/translation/react/${namespace}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe wish list
     * @name GetRecipeWishlistsUsingGet
     * @summary Get recipe wishlists for current user
     * @request GET:/axfood/rest/user-recipe-wishlist
     * @secure
     */
    this.getRecipeWishlistsUsingGet = (params = {}) => this.request({
      path: `/axfood/rest/user-recipe-wishlist`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe wish list
     * @name CreateRecipeWishlistUsingPost
     * @summary Create user recipe wish list
     * @request POST:/axfood/rest/user-recipe-wishlist
     * @secure
     */
    this.createRecipeWishlistUsingPost = (recipeWishlistForm, params = {}) => this.request({
      path: `/axfood/rest/user-recipe-wishlist`,
      method: "POST",
      body: recipeWishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe wish list
     * @name GetRecipeWishlistUsingGet
     * @summary Get user wish list
     * @request GET:/axfood/rest/user-recipe-wishlist/{id}
     * @secure
     */
    this.getRecipeWishlistUsingGet = (id, params = {}) => this.request({
      path: `/axfood/rest/user-recipe-wishlist/${id}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe wish list
     * @name DeleteRecipeWishlistUsingDelete
     * @summary Delete user wish list
     * @request DELETE:/axfood/rest/user-recipe-wishlist/{id}
     * @secure
     */
    this.deleteRecipeWishlistUsingDelete = (id, params = {}) => this.request({
      path: `/axfood/rest/user-recipe-wishlist/${id}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe wish list
     * @name AddRecipeToRecipeWishlistUsingPost
     * @summary Add recipe to user wish list
     * @request POST:/axfood/rest/user-recipe-wishlist/{id}/add/{recipeCode}
     * @secure
     */
    this.addRecipeToRecipeWishlistUsingPost = (id, recipeCode, params = {}) => this.request({
      path: `/axfood/rest/user-recipe-wishlist/${id}/add/${recipeCode}`,
      method: "POST",
      secure: true,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe wish list
     * @name RemoveEntryFromRecipeWishlistUsingDelete
     * @summary Remove recipe from recipe wish list
     * @request DELETE:/axfood/rest/user-recipe-wishlist/{listId}/recipe/{recipeCode}
     * @secure
     */
    this.removeEntryFromRecipeWishlistUsingDelete = (listId, recipeCode, params = {}) => this.request({
      path: `/axfood/rest/user-recipe-wishlist/${listId}/recipe/${recipeCode}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetUserWishListsUsingGet
     * @summary Get user wish lists
     * @request GET:/axfood/rest/user-wishlist
     * @secure
     */
    this.getUserWishListsUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name CreateWishlistUsingPost
     * @summary Create user wish list
     * @request POST:/axfood/rest/user-wishlist
     * @secure
     */
    this.createWishlistUsingPost = (wishlistForm, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist`,
      method: "POST",
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetUserWishlistFilterNonSalableUsingGet
     * @summary Get user wish list filter non salable
     * @request GET:/axfood/rest/user-wishlist-filter-non-salable/{id}
     * @secure
     */
    this.getUserWishlistFilterNonSalableUsingGet = (id, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist-filter-non-salable/${id}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name DeleteUserWishListsUsingDelete
     * @summary Delete multiple user wishlists
     * @request DELETE:/axfood/rest/user-wishlist/remove-many
     * @secure
     */
    this.deleteUserWishListsUsingDelete = (wishlistIds, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/remove-many`,
      method: "DELETE",
      body: wishlistIds,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name GetUserWishlistUsingGet
     * @summary Get user wish list
     * @request GET:/axfood/rest/user-wishlist/{id}
     * @secure
     */
    this.getUserWishlistUsingGet = (id, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${id}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name UpdateWishlistUsingPost
     * @summary Check entries availability
     * @request POST:/axfood/rest/user-wishlist/{id}
     * @secure
     */
    this.updateWishlistUsingPost = (id, wishlistForm, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${id}`,
      method: "POST",
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name DeleteUserWishListUsingDelete
     * @summary Delete user wish list
     * @request DELETE:/axfood/rest/user-wishlist/{id}
     * @secure
     */
    this.deleteUserWishListUsingDelete = (id, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${id}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name AddProductsToUserWishlistUsingPost
     * @summary Add products to user wish list
     * @request POST:/axfood/rest/user-wishlist/{id}/add
     * @secure
     */
    this.addProductsToUserWishlistUsingPost = (id, wishlistForm, query, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${id}/add`,
      method: "POST",
      query,
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name CheckEntriesInUserWishlistUsingPut
     * @summary Check entries in user wish list
     * @request PUT:/axfood/rest/user-wishlist/{id}/check
     * @secure
     */
    this.checkEntriesInUserWishlistUsingPut = (id, wishlistForm, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${id}/check`,
      method: "PUT",
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name UpdateWishlistSortingUsingPut
     * @summary Update wish list sorting
     * @request PUT:/axfood/rest/user-wishlist/{id}/sorting
     * @secure
     */
    this.updateWishlistSortingUsingPut = (id, wishlistForm, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${id}/sorting`,
      method: "PUT",
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name RemoveEntryFromUserWishlistUsingDelete
     * @summary Remove entry from user wish list
     * @request DELETE:/axfood/rest/user-wishlist/{listId}/product/{productCode}
     * @secure
     */
    this.removeEntryFromUserWishlistUsingDelete = (listId, productCode, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${listId}/product/${productCode}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name RemoveEntriesFromUserWishlistUsingDelete
     * @summary Remove entries from user wish list
     * @request DELETE:/axfood/rest/user-wishlist/{listId}/products
     * @secure
     */
    this.removeEntriesFromUserWishlistUsingDelete = (listId, identifiers, params = {}) => this.request({
      path: `/axfood/rest/user-wishlist/${listId}/products`,
      method: "DELETE",
      body: identifiers,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name GetValidatedAddressUsingGet
     * @summary Validate address
     * @request GET:/axfood/rest/validate
     * @secure
     */
    this.getValidatedAddressUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/validate`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Postal
     * @name GetValidatedAddressLineUsingGet
     * @summary Validate address line
     * @request GET:/axfood/rest/validate/addressline
     * @secure
     */
    this.getValidatedAddressLineUsingGet = (query, params = {}) => this.request({
      path: `/axfood/rest/validate/addressline`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name AddSiteWishlistToUserWishlistUsingPost
     * @summary Add site wish list to user wish list
     * @request POST:/axfood/rest/wishlist-to-wishlist
     * @secure
     */
    this.addSiteWishlistToUserWishlistUsingPost = (query, params = {}) => this.request({
      path: `/axfood/rest/wishlist-to-wishlist`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name CheckEntriesAvailabilityUsingGet
     * @summary Check entries availability
     * @request GET:/axfood/rest/wishlist/changes/{id}
     * @secure
     */
    this.checkEntriesAvailabilityUsingGet = (id, params = {}) => this.request({
      path: `/axfood/rest/wishlist/changes/${id}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name ValidateWishlistEntriesUsingPost
     * @summary Validate list entries, with replacement for conflicts
     * @request POST:/axfood/rest/wishlist/validate-entries
     * @secure
     */
    this.validateWishlistEntriesUsingPost = (wishlistForm, query, params = {}) => this.request({
      path: `/axfood/rest/wishlist/validate-entries`,
      method: "POST",
      query,
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name ValidateOrderEntriesUsingGet1
     * @summary Validate list entries, with replacement for conflicts
     * @request GET:/axfood/rest/wishlist/validate/{listId}
     * @secure
     */
    this.validateOrderEntriesUsingGet1 = (listId, query, params = {}) => this.request({
      path: `/axfood/rest/wishlist/validate/${listId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Wish list
     * @name AddProductsToCartUsingPost
     * @summary Add product to cart from wish list
     * @request POST:/axfood/rest/wishlist/{id}/cart
     * @secure
     */
    this.addProductsToCartUsingPost = (id, wishlistForm, params = {}) => this.request({
      path: `/axfood/rest/wishlist/${id}/cart`,
      method: "POST",
      body: wishlistForm,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/Login.ts
var Login = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Password Reset
     * @name ChangePasswordUsingPost
     * @summary Change
     * @request POST:/login/pw/change
     * @secure
     */
    this.changePasswordUsingPost = (query, params = {}) => this.request({
      path: `/login/pw/change`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name CheckCustomerExistsUsingPost
     * @summary Check Existing Customer
     * @request POST:/login/pw/checkExistingCustomer
     * @secure
     */
    this.checkCustomerExistsUsingPost = (form, params = {}) => this.request({
      path: `/login/pw/checkExistingCustomer`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name CheckExistingB2BCustomerUsingGet
     * @summary Check B2B Customer
     * @request GET:/login/pw/checkb2bcustomer
     * @secure
     */
    this.checkExistingB2BCustomerUsingGet = (query, params = {}) => this.request({
      path: `/login/pw/checkb2bcustomer`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name CheckExistingB2BCustomerBySapIdUsingGet
     * @summary Check B2B Customer By Sapid
     * @request GET:/login/pw/checkb2bcustomerbysapid
     * @secure
     */
    this.checkExistingB2BCustomerBySapIdUsingGet = (query, params = {}) => this.request({
      path: `/login/pw/checkb2bcustomerbysapid`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name CheckExistingB2BCustomerBySapIdOrAxboIdUsingGet
     * @summary Check B2B Customer By Sapid Or AxboId
     * @request GET:/login/pw/checkb2bcustomerbysapidOrAxboId
     * @secure
     */
    this.checkExistingB2BCustomerBySapIdOrAxboIdUsingGet = (query, params = {}) => this.request({
      path: `/login/pw/checkb2bcustomerbysapidOrAxboId`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name CheckExistingCustomerUsingGet
     * @summary Check Customer
     * @request GET:/login/pw/checkcustomer
     * @deprecated
     * @secure
     */
    this.checkExistingCustomerUsingGet = (query, params = {}) => this.request({
      path: `/login/pw/checkcustomer`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name VerifyNewCustomerUsingPost
     * @summary Recover account
     * @request POST:/login/pw/recoveraccount
     * @deprecated
     * @secure
     */
    this.verifyNewCustomerUsingPost = (form, params = {}) => this.request({
      path: `/login/pw/recoveraccount`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name RecoverB2BCustomerUsingPost
     * @summary Recover B2B Account
     * @request POST:/login/pw/recoverb2baccount
     * @secure
     */
    this.recoverB2BCustomerUsingPost = (form, params = {}) => this.request({
      path: `/login/pw/recoverb2baccount`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name ValidateEmailTokenUsingPost
     * @summary Validate generated pwd reset Token
     * @request POST:/login/pw/validatetoken
     * @secure
     */
    this.validateEmailTokenUsingPost = (query, params = {}) => this.request({
      path: `/login/pw/validatetoken`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Password Reset
     * @name VerifySmsTokenUsingPost
     * @summary Verify SMS Token
     * @request POST:/login/pw/verifysmstoken
     * @secure
     */
    this.verifySmsTokenUsingPost = (form, params = {}) => this.request({
      path: `/login/pw/verifysmstoken`,
      method: "POST",
      body: form,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/C.ts
var C = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Category
     * @name GetCategoryUsingGet
     * @summary Get category
     * @request GET:/c/{categoryPath}
     * @secure
     */
    this.getCategoryUsingGet = (categoryPath, query, params = {}) => this.request({
      path: `/c/${categoryPath}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Category
     * @name GetResultsUsingGet
     * @summary Get products for category
     * @request GET:/c/{categoryPath}/results
     * @secure
     */
    this.getResultsUsingGet = (categoryCode, categoryPath, query, params = {}) => this.request({
      path: `/c/${categoryPath}/results`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
  }
};

// src/storefront/ArticleNavigationMenuComponent.ts
var ArticleNavigationMenuComponent = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Component
     * @name GetNavigationDataUsingGet
     * @summary Get navigation data for the given component and page
     * @request GET:/articleNavigationMenuComponent/{componentId}
     * @secure
     */
    this.getNavigationDataUsingGet = (componentId, query, params = {}) => this.request({
      path: `/articleNavigationMenuComponent/${componentId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/ProductCarouselComponent.ts
var ProductCarouselComponent = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Component
     * @name GetPackagesForProductBannerUsingGet1
     * @summary Get packages for product banner
     * @request GET:/productCarouselComponent/packages/{componentId}
     * @secure
     */
    this.getPackagesForProductBannerUsingGet1 = (componentId, params = {}) => this.request({
      path: `/productCarouselComponent/packages/${componentId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetProductsForProductBannerUsingGet1
     * @summary Get products for product banner
     * @request GET:/productCarouselComponent/{componentId}
     * @secure
     */
    this.getProductsForProductBannerUsingGet1 = (componentId, query, params = {}) => this.request({
      path: `/productCarouselComponent/${componentId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/BuyAllProductsComponent.ts
var BuyAllProductsComponent = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Component
     * @name GetProductsForBuyAllBannerUsingGet
     * @summary Get products for a buy all component
     * @request GET:/buyAllProductsComponent/{componentId}
     * @secure
     */
    this.getProductsForBuyAllBannerUsingGet = (componentId, params = {}) => this.request({
      path: `/buyAllProductsComponent/${componentId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/ProductBannerComponent.ts
var ProductBannerComponent = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Component
     * @name GetPackagesForProductBannerUsingGet
     * @summary Get packages for product banner
     * @request GET:/productBannerComponent/packages/{componentId}
     * @secure
     */
    this.getPackagesForProductBannerUsingGet = (componentId, params = {}) => this.request({
      path: `/productBannerComponent/packages/${componentId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetProductsForProductBannerUsingGet
     * @summary Get products for product banner
     * @request GET:/productBannerComponent/{componentId}
     * @secure
     */
    this.getProductsForProductBannerUsingGet = (componentId, query, params = {}) => this.request({
      path: `/productBannerComponent/${componentId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/LeftMenu.ts
var LeftMenu = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Component
     * @name GetCategoryNavigationNodeDataUsingGet
     * @summary Get category navigation node data
     * @request GET:/leftMenu/categories
     * @secure
     */
    this.getCategoryNavigationNodeDataUsingGet = (componentId, params = {}) => this.request({
      path: `/leftMenu/categories`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetCategoryNavigationNodeDataUsingGet1
     * @summary Get category navigation node data
     * @request GET:/leftMenu/categories/{componentId}
     * @secure
     */
    this.getCategoryNavigationNodeDataUsingGet1 = (componentId, params = {}) => this.request({
      path: `/leftMenu/categories/${componentId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetCategoryNavigationTreeUsingGet
     * @summary Get category navigation tree
     * @request GET:/leftMenu/categorytree
     * @secure
     */
    this.getCategoryNavigationTreeUsingGet = (query, params = {}) => this.request({
      path: `/leftMenu/categorytree`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetLinksNavigationNodeDataUsingGet
     * @summary Get links navigation node data
     * @request GET:/leftMenu/links
     * @secure
     */
    this.getLinksNavigationNodeDataUsingGet = (componentId, params = {}) => this.request({
      path: `/leftMenu/links`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Component
     * @name GetLinksNavigationNodeDataUsingGet1
     * @summary Get links navigation node data
     * @request GET:/leftMenu/links/{componentId}
     * @secure
     */
    this.getLinksNavigationNodeDataUsingGet1 = (componentId, params = {}) => this.request({
      path: `/leftMenu/links/${componentId}`,
      method: "GET",
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/storefront/Search.ts
var Search = class extends HttpClient {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Product
     * @name GetSearchResultsUsingGet1
     * @summary Get search results for products
     * @request GET:/search
     * @secure
     */
    this.getSearchResultsUsingGet1 = (query, params = {}) => this.request({
      path: `/search`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetSearchResultsAsYouTypeUsingGet
     * @summary Get search results for products for as you type
     * @request GET:/search/asutype
     * @secure
     */
    this.getSearchResultsAsYouTypeUsingGet = (query, params = {}) => this.request({
      path: `/search/asutype`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetAutocompleteSuggestionsUsingGet
     * @summary Get autocomplete suggestions
     * @request GET:/search/autocomplete/{componentUid}
     * @secure
     */
    this.getAutocompleteSuggestionsUsingGet = (componentUid, query, params = {}) => this.request({
      path: `/search/autocomplete/${componentUid}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetPromotedProductsUsingGet
     * @summary Get promoted products
     * @request GET:/search/campaigns
     * @secure
     */
    this.getPromotedProductsUsingGet = (query, params = {}) => this.request({
      path: `/search/campaigns`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetMixedCampaignsUsingGet
     * @summary Get online and offline campaigns
     * @request GET:/search/campaigns/mix
     * @secure
     */
    this.getMixedCampaignsUsingGet = (query, params = {}) => this.request({
      path: `/search/campaigns/mix`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetOfflineCampaignsUsingGet
     * @summary Get offline campaigns
     * @request GET:/search/campaigns/offline
     * @secure
     */
    this.getOfflineCampaignsUsingGet = (query, params = {}) => this.request({
      path: `/search/campaigns/offline`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetOnlineCampaignsUsingGet
     * @summary Get online campaigns
     * @request GET:/search/campaigns/online
     * @secure
     */
    this.getOnlineCampaignsUsingGet = (query, params = {}) => this.request({
      path: `/search/campaigns/online`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Promotion
     * @name GetOfflineCampaignsPreviewUsingGet
     * @summary Get promoted previews
     * @request GET:/search/campaigns/preview/offline
     * @secure
     */
    this.getOfflineCampaignsPreviewUsingGet = (query, params = {}) => this.request({
      path: `/search/campaigns/preview/offline`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetSearchCleanResultsUsingGet
     * @summary Get clean search results for products
     * @request GET:/search/clean
     * @secure
     */
    this.getSearchCleanResultsUsingGet = (query, params = {}) => this.request({
      path: `/search/clean`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetFacetsUsingGet
     * @summary Get facets for product
     * @request GET:/search/facets
     * @secure
     */
    this.getFacetsUsingGet = (query, params = {}) => this.request({
      path: `/search/facets`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetMultiSearchResultsAsYouTypeUsingGet
     * @summary Get search results for products and related navigation categories for as you type and autoComplete results
     * @request GET:/search/multisearchAsutype
     * @secure
     */
    this.getMultiSearchResultsAsYouTypeUsingGet = (query, params = {}) => this.request({
      path: `/search/multisearchAsutype`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetMultiSearchResultsUsingGet
     * @summary Get search results for products and related navigation categories for as you type and autoComplete results
     * @request GET:/search/multisearchComplete
     * @secure
     */
    this.getMultiSearchResultsUsingGet = (query, params = {}) => this.request({
      path: `/search/multisearchComplete`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Recipe
     * @name GetSearchResultsUsingGet
     * @summary Searches for recipes
     * @request GET:/search/recipe
     * @secure
     */
    this.getSearchResultsUsingGet = (query, params = {}) => this.request({
      path: `/search/recipe`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetRelatedResultsUsingGet
     * @summary Get related search results for products
     * @request GET:/search/related-results
     * @secure
     */
    this.getRelatedResultsUsingGet = (query, params = {}) => this.request({
      path: `/search/related-results`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * No description
     *
     * @tags Product
     * @name GetResultsUsingGet1
     * @summary Get results for products
     * @request GET:/search/results
     * @secure
     */
    this.getResultsUsingGet1 = (query, params = {}) => this.request({
      path: `/search/results`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
  }
};

// src/cms/http-client.ts
var HttpClient2 = class {
  constructor(apiConfig = {}) {
    this.baseUrl = "//www.willys.inttest.occdev.axfood.se/axfoodcommercewebservices/v2";
    this.securityData = null;
    this.abortControllers = /* @__PURE__ */ new Map();
    this.customFetch = (...fetchParams) => fetch(...fetchParams);
    this.baseApiParams = {
      credentials: "same-origin",
      headers: {},
      redirect: "follow",
      referrerPolicy: "no-referrer"
    };
    this.setSecurityData = (data) => {
      this.securityData = data;
    };
    this.contentFormatters = {
      ["application/json" /* Json */]: (input) => input !== null && (typeof input === "object" || typeof input === "string") ? JSON.stringify(input) : input,
      ["text/plain" /* Text */]: (input) => input !== null && typeof input !== "string" ? JSON.stringify(input) : input,
      ["multipart/form-data" /* FormData */]: (input) => Object.keys(input || {}).reduce((formData, key) => {
        const property = input[key];
        formData.append(
          key,
          property instanceof Blob ? property : typeof property === "object" && property !== null ? JSON.stringify(property) : `${property}`
        );
        return formData;
      }, new FormData()),
      ["application/x-www-form-urlencoded" /* UrlEncoded */]: (input) => this.toQueryString(input)
    };
    this.createAbortSignal = (cancelToken) => {
      if (this.abortControllers.has(cancelToken)) {
        const abortController2 = this.abortControllers.get(cancelToken);
        if (abortController2) {
          return abortController2.signal;
        }
        return void 0;
      }
      const abortController = new AbortController();
      this.abortControllers.set(cancelToken, abortController);
      return abortController.signal;
    };
    this.abortRequest = (cancelToken) => {
      const abortController = this.abortControllers.get(cancelToken);
      if (abortController) {
        abortController.abort();
        this.abortControllers.delete(cancelToken);
      }
    };
    this.request = async ({
      body,
      secure,
      path,
      type,
      query,
      format,
      baseUrl,
      cancelToken,
      ...params
    }) => {
      const secureParams = (typeof secure === "boolean" ? secure : this.baseApiParams.secure) && this.securityWorker && await this.securityWorker(this.securityData) || {};
      const requestParams = this.mergeRequestParams(params, secureParams);
      const queryString = query && this.toQueryString(query);
      const payloadFormatter = this.contentFormatters[type || "application/json" /* Json */];
      const responseFormat = format || requestParams.format;
      return this.customFetch(`${baseUrl || this.baseUrl || ""}${path}${queryString ? `?${queryString}` : ""}`, {
        ...requestParams,
        headers: {
          ...requestParams.headers || {},
          ...type && type !== "multipart/form-data" /* FormData */ ? { "Content-Type": type } : {}
        },
        signal: (cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal) || null,
        body: typeof body === "undefined" || body === null ? null : payloadFormatter(body)
      }).then(async (response) => {
        const r = response.clone();
        r.data = null;
        r.error = null;
        const data = !responseFormat ? r : await response[responseFormat]().then((data2) => {
          if (r.ok) {
            r.data = data2;
          } else {
            r.error = data2;
          }
          return r;
        }).catch((e) => {
          r.error = e;
          return r;
        });
        if (cancelToken) {
          this.abortControllers.delete(cancelToken);
        }
        if (!response.ok) throw data;
        return data;
      });
    };
    Object.assign(this, apiConfig);
  }
  encodeQueryParam(key, value) {
    const encodedKey = encodeURIComponent(key);
    return `${encodedKey}=${encodeURIComponent(typeof value === "number" ? value : `${value}`)}`;
  }
  addQueryParam(query, key) {
    return this.encodeQueryParam(key, query[key]);
  }
  addArrayQueryParam(query, key) {
    const value = query[key];
    return value.map((v) => this.encodeQueryParam(key, v)).join("&");
  }
  toQueryString(rawQuery) {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter((key) => "undefined" !== typeof query[key]);
    return keys.map((key) => Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key)).join("&");
  }
  addQueryParams(rawQuery) {
    const queryString = this.toQueryString(rawQuery);
    return queryString ? `?${queryString}` : "";
  }
  mergeRequestParams(params1, params2) {
    return {
      ...this.baseApiParams,
      ...params1,
      ...params2 || {},
      headers: {
        ...this.baseApiParams.headers || {},
        ...params1.headers || {},
        ...params2 && params2.headers || {}
      }
    };
  }
};

// src/cms/BaseSiteId.ts
var BaseSiteId = class extends HttpClient2 {
  constructor() {
    super(...arguments);
    /**
     * @description Lists supported payment card types.
     *
     * @tags Miscs
     * @name GetCardTypesUsingGet
     * @summary Get a list of supported payment card types.
     * @request GET:/{baseSiteId}/cardtypes
     * @secure
     */
    this.getCardTypesUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/cardtypes`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns all catalogs with versions defined for the base store.
     *
     * @tags Catalogs
     * @name GetCatalogsUsingGet
     * @summary Get a list of catalogs
     * @request GET:/{baseSiteId}/catalogs
     * @secure
     */
    this.getCatalogsUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/catalogs`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns information about a catalog based on its ID, along with the versions defined for the current base store.
     *
     * @tags Catalogs
     * @name GetCatalogUsingGet
     * @summary Get a catalog
     * @request GET:/{baseSiteId}/catalogs/{catalogId}
     * @secure
     */
    this.getCatalogUsingGet = (catalogId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/catalogs/${catalogId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns information about the catalog version that exists for the current base store.
     *
     * @tags Catalogs
     * @name GetCatalogVersionUsingGet
     * @summary Get information about catalog version
     * @request GET:/{baseSiteId}/catalogs/{catalogId}/{catalogVersionId}
     * @secure
     */
    this.getCatalogVersionUsingGet = (catalogId, catalogVersionId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/catalogs/${catalogId}/${catalogVersionId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns information about a specified category that exists in a catalog version available for the current base store.
     *
     * @tags Catalogs
     * @name GetCategoriesUsingGet
     * @summary Get information about catagory in a catalog version
     * @request GET:/{baseSiteId}/catalogs/{catalogId}/{catalogVersionId}/categories/{categoryId}
     * @secure
     */
    this.getCategoriesUsingGet = (catalogId, catalogVersionId, categoryId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/catalogs/${catalogId}/${catalogVersionId}/categories/${categoryId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Given a list of component identifiers, return cms component data.
     *
     * @tags Component
     * @name FindComponentsByIds
     * @summary Find component data by id(s)
     * @request GET:/{baseSiteId}/cms/components
     * @secure
     */
    this.findComponentsByIds = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/cms/components`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Given a list of component identifiers in body, return cms component data.
     *
     * @tags Component
     * @name SearchComponentsByIds
     * @summary Get components' data by id given in body
     * @request POST:/{baseSiteId}/cms/components
     * @secure
     */
    this.searchComponentsByIds = (baseSiteId, componentIdList, query, params = {}) => this.request({
      path: `/${baseSiteId}/cms/components`,
      method: "POST",
      query,
      body: componentIdList,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Given a component identifier, return cms component data.
     *
     * @tags Component
     * @name GetComponentById
     * @summary Get component data
     * @request GET:/{baseSiteId}/cms/components/{componentId}
     * @secure
     */
    this.getComponentById = (componentId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/cms/components/${componentId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Given a page identifier, return the page data with a list of cms content slots, each of which contains a list of cms component data.
     *
     * @tags Page
     * @name GetPage
     * @summary Get page data with list of cms content slots
     * @request GET:/{baseSiteId}/cms/pages
     * @secure
     */
    this.getPage = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/cms/pages`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Given a page identifier, return the page data with a list of cms content slots, each of which contains a list of cms component data.
     *
     * @tags Page
     * @name GetPageById
     * @summary Get page data with list of cms content slots
     * @request GET:/{baseSiteId}/cms/pages/{pageId}
     * @secure
     */
    this.getPageById = (pageId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/cms/pages/${pageId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Lists all available currencies (all usable currencies for the current store). If the list of currencies for a base store is empty, a list of all currencies available in the system is returned.
     *
     * @tags Miscs
     * @name GetCurrenciesUsingGet
     * @summary Get a list of available currencies.
     * @request GET:/{baseSiteId}/currencies
     * @secure
     */
    this.getCurrenciesUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/currencies`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns all customer groups that are direct subgroups of a customergroup. To try out the methods of the Customer Groups controller, you must authorize a user who belongs to the “customermanagergroup”.
     *
     * @tags Customer Groups
     * @name GetAllCustomerGroupsUsingGet
     * @summary Get all subgroups of a customergroup.
     * @request GET:/{baseSiteId}/customergroups
     * @secure
     */
    this.getAllCustomerGroupsUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/customergroups`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Creates a new customer group that is a direct subgroup of a customergroup. To try out the methods of the Customer Groups controller, you must authorize a user who belongs to the “customermanagergroup”.
     *
     * @tags Customer Groups
     * @name CreateNewCustomerGroupUsingPost1
     * @summary Creates a new customer group.
     * @request POST:/{baseSiteId}/customergroups
     * @secure
     */
    this.createNewCustomerGroupUsingPost1 = (baseSiteId, userGroup, params = {}) => this.request({
      path: `/${baseSiteId}/customergroups`,
      method: "POST",
      body: userGroup,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns a customer group with a specific groupId. To try out the methods of the Customer Groups controller, you must authorize a user who belongs to the “customermanagergroup”.
     *
     * @tags Customer Groups
     * @name GetCustomerGroupUsingGet
     * @summary Get a specific customer group.
     * @request GET:/{baseSiteId}/customergroups/{groupId}
     * @secure
     */
    this.getCustomerGroupUsingGet = (groupId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/customergroups/${groupId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Sets members for a user group. The list of existing members is overwritten with a new one. To try out the methods of the Customer Groups controller, you must authorize a user who belongs to the “customermanagergroup”.
     *
     * @tags Customer Groups
     * @name SetUserListForCustomerGroupUsingPut1
     * @summary Sets members for a user group.
     * @request PUT:/{baseSiteId}/customergroups/{groupId}/members
     * @secure
     */
    this.setUserListForCustomerGroupUsingPut1 = (groupId, baseSiteId, members, params = {}) => this.request({
      path: `/${baseSiteId}/customergroups/${groupId}/members`,
      method: "PUT",
      body: members,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Assigns user(s) to a customer group. To try out the methods of the Customer Groups controller, you must authorize a user who belongs to the “customermanagergroup”.
     *
     * @tags Customer Groups
     * @name AssignUserToCustomerGroupUsingPatch1
     * @summary Assigns user(s) to a customer group.
     * @request PATCH:/{baseSiteId}/customergroups/{groupId}/members
     * @secure
     */
    this.assignUserToCustomerGroupUsingPatch1 = (groupId, baseSiteId, members, params = {}) => this.request({
      path: `/${baseSiteId}/customergroups/${groupId}/members`,
      method: "PATCH",
      body: members,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes user from a customer group. To try out the methods of the Customer Groups controller, you must authorize a user who belongs to the “customermanagergroup”.
     *
     * @tags Customer Groups
     * @name RemoveUsersFromCustomerGroupUsingDelete
     * @summary Delete a user from a customer group.
     * @request DELETE:/{baseSiteId}/customergroups/{groupId}/members/{userId}
     * @secure
     */
    this.removeUsersFromCustomerGroupUsingDelete = (groupId, userId, baseSiteId, params = {}) => this.request({
      path: `/${baseSiteId}/customergroups/${groupId}/members/${userId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Lists all supported delivery countries for the current store. The list is sorted alphabetically.
     *
     * @tags Miscs
     * @name GetDeliveryCountriesUsingGet
     * @summary Get a list of supported countries.
     * @request GET:/{baseSiteId}/deliverycountries
     * @secure
     */
    this.getDeliveryCountriesUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/deliverycountries`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Used for product export. Depending on the timestamp parameter, it can return all products or only products modified after the given time.
     *
     * @tags Export
     * @name ExportProductsUsingGet
     * @summary Get a list of product exports.
     * @request GET:/{baseSiteId}/export/products
     * @secure
     */
    this.exportProductsUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/export/products`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns the orders that have changed status. Returns only the elements from the current baseSite that have been updated after the provided timestamp.
     *
     * @tags Feeds
     * @name OrderStatusFeedUsingGet
     * @summary Get a list of orders with status updates
     * @request GET:/{baseSiteId}/feeds/orders/statusfeed
     * @secure
     */
    this.orderStatusFeedUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/feeds/orders/statusfeed`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Generates a token to restore a customer's forgotten password.
     *
     * @tags Forgotten Passwords
     * @name RestorePasswordUsingPost
     * @summary Generates a token to restore a customer's forgotten password.
     * @request POST:/{baseSiteId}/forgottenpasswordtokens
     * @secure
     */
    this.restorePasswordUsingPost = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/forgottenpasswordtokens`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Lists all available languages (all languages used for a particular store). If the list of languages for a base store is empty, a list of all languages available in the system will be returned.
     *
     * @tags Miscs
     * @name GetLanguagesUsingGet
     * @summary Get a list of available languages.
     * @request GET:/{baseSiteId}/languages
     * @secure
     */
    this.getLanguagesUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/languages`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns details of a specific order based on the order GUID (Globally Unique Identifier) or the order CODE. The response contains detailed order information.
     *
     * @tags Orders
     * @name GetOrderUsingGet
     * @summary Get a order
     * @request GET:/{baseSiteId}/orders/{code}
     * @secure
     */
    this.getOrderUsingGet = (code, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/orders/${code}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns products added to the express update feed. Returns only elements updated after the provided timestamp. The queue is cleared using a defined cronjob.
     *
     * @tags Products
     * @name ExpressUpdateUsingGet
     * @summary Get products added to the express update feed
     * @request GET:/{baseSiteId}/products/expressupdate
     * @secure
     */
    this.expressUpdateUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/expressupdate`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns a list of products and additional data, such as available facets, available sorting, and pagination options. It can also include spelling suggestions. To make spelling suggestions work, you need to make sure that "enableSpellCheck" on the SearchQuery is set to "true" (by default, it should already be set to "true"). You also need to have indexed properties configured to be used for spellchecking.
     *
     * @tags Products
     * @name SearchProductsUsingGet
     * @summary Get a list of products and additional data
     * @request GET:/{baseSiteId}/products/search
     * @secure
     */
    this.searchProductsUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/search`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description In the response header, the "x-total-count" indicates the total number of products satisfying a query.
     *
     * @tags Products
     * @name CountSearchProductsUsingHead
     * @summary Get a header with total number of products
     * @request HEAD:/{baseSiteId}/products/search
     * @secure
     */
    this.countSearchProductsUsingHead = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/search`,
      method: "HEAD",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns a list of all available suggestions related to a given term and limits the results to a specific value of the max parameter.
     *
     * @tags Products
     * @name GetSuggestionsUsingGet
     * @summary Get a list of available suggestions
     * @request GET:/{baseSiteId}/products/suggestions
     * @secure
     */
    this.getSuggestionsUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/suggestions`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns details of a single product according to a product code.
     *
     * @tags Products
     * @name GetProductByCodeUsingGet
     * @summary Get product details
     * @request GET:/{baseSiteId}/products/{productCode}
     * @secure
     */
    this.getProductByCodeUsingGet = (productCode, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns references for a product with a given product code. Reference type specifies which references to return.
     *
     * @tags Products
     * @name ExportProductReferencesUsingGet
     * @summary Get a product reference
     * @request GET:/{baseSiteId}/products/{productCode}/references
     * @secure
     */
    this.exportProductReferencesUsingGet = (productCode, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}/references`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns the reviews for a product with a given product code.
     *
     * @tags Products
     * @name GetProductReviewsUsingGet
     * @summary Get reviews for a product
     * @request GET:/{baseSiteId}/products/{productCode}/reviews
     * @secure
     */
    this.getProductReviewsUsingGet = (productCode, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}/reviews`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Creates a new customer review as an anonymous user.
     *
     * @tags Products
     * @name CreateReviewUsingPost1
     * @summary Creates a new customer review as an anonymous user
     * @request POST:/{baseSiteId}/products/{productCode}/reviews
     * @secure
     */
    this.createReviewUsingPost1 = (productCode, baseSiteId, review, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}/reviews`,
      method: "POST",
      query,
      body: review,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns a product's stock levels sorted by distance from the specified location, which is provided using the free-text "location" parameter, or by using the longitude and latitude parameters. The following two sets of parameters are available: location (required), currentPage (optional), pageSize (optional); or longitude (required), latitude (required), currentPage (optional), pageSize(optional).
     *
     * @tags Products
     * @name SearchProductStockByLocationUsingGet
     * @summary Get a product's stock level
     * @request GET:/{baseSiteId}/products/{productCode}/stock
     * @secure
     */
    this.searchProductStockByLocationUsingGet = (productCode, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}/stock`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description In the response header, the "x-total-count" indicates the total number of a product's stock levels. The following two sets of parameters are available: location (required); or longitude (required), and latitude (required).
     *
     * @tags Products
     * @name CountSearchProductStockByLocationUsingHead
     * @summary Get header with a total number of product's stock levels
     * @request HEAD:/{baseSiteId}/products/{productCode}/stock
     * @secure
     */
    this.countSearchProductStockByLocationUsingHead = (productCode, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}/stock`,
      method: "HEAD",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns a product's stock level for a particular store (in other words, for a particular point of sale).
     *
     * @tags Products
     * @name GetStockDataUsingGet
     * @summary Get a product's stock level for a store
     * @request GET:/{baseSiteId}/products/{productCode}/stock/{storeName}
     * @secure
     */
    this.getStockDataUsingGet = (baseSiteId, productCode, storeName, query, params = {}) => this.request({
      path: `/${baseSiteId}/products/${productCode}/stock/${storeName}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns promotions defined for a current base site. Requests pertaining to promotions have been developed for the previous version of promotions and vouchers and therefore some of them are currently not compatible with the new promotion engine.
     *
     * @tags Promotions
     * @name GetPromotionsUsingGet1
     * @summary Get a list of promotions
     * @request GET:/{baseSiteId}/promotions
     * @secure
     */
    this.getPromotionsUsingGet1 = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/promotions`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns details of a single promotion specified by a promotion code. Requests pertaining to promotions have been developed for the previous version of promotions and vouchers and therefore some of them are currently not compatible with the new promotion engine.
     *
     * @tags Promotions
     * @name GetPromotionByCodeUsingGet
     * @summary Get a promotion based on code
     * @request GET:/{baseSiteId}/promotions/{code}
     * @secure
     */
    this.getPromotionByCodeUsingGet = (code, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/promotions/${code}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Lists all store locations that are near the location specified in a query or based on latitude and longitude.
     *
     * @tags Stores
     * @name LocationSearchUsingGet
     * @summary Get a list of store locations
     * @request GET:/{baseSiteId}/stores
     * @secure
     */
    this.locationSearchUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/stores`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description In the response header, the "x-total-count" indicates the number of all store locations that are near the location specified in a query, or based on latitude and longitude.
     *
     * @tags Stores
     * @name CountLocationSearchUsingHead
     * @summary Get a header with the number of store locations
     * @request HEAD:/{baseSiteId}/stores
     * @secure
     */
    this.countLocationSearchUsingHead = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/stores`,
      method: "HEAD",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns store location based on its unique name.
     *
     * @tags Stores
     * @name LocationDetailsUsingGet
     * @summary Get a store location
     * @request GET:/{baseSiteId}/stores/{storeId}
     * @secure
     */
    this.locationDetailsUsingGet = (storeId, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/stores/${storeId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Lists all localized titles.
     *
     * @tags Miscs
     * @name GetTitlesUsingGet
     * @summary Get a list of all localized titles.
     * @request GET:/{baseSiteId}/titles
     * @secure
     */
    this.getTitlesUsingGet = (baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/titles`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Registers a customer. There are two options for registering a customer. The first option requires the following parameters: login, password, firstName, lastName, titleCode. The second option converts a guest to a customer. In this case, the required parameters are: guid, password.
     *
     * @tags Users
     * @name RegisterUserUsingPost1
     * @summary  Registers a customer
     * @request POST:/{baseSiteId}/users
     * @secure
     */
    this.registerUserUsingPost1 = (baseSiteId, user, query, params = {}) => this.request({
      path: `/${baseSiteId}/users`,
      method: "POST",
      query,
      body: user,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns customer profile.
     *
     * @tags Users
     * @name GetUserUsingGet
     * @summary Get customer profile
     * @request GET:/{baseSiteId}/users/{userId}
     * @secure
     */
    this.getUserUsingGet = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Updates customer profile. Attributes not provided in the request body will be defined again (set to null or default).
     *
     * @tags Users
     * @name PutUserUsingPut
     * @summary Updates customer profile
     * @request PUT:/{baseSiteId}/users/{userId}
     * @secure
     */
    this.putUserUsingPut = (baseSiteId, userId, user, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}`,
      method: "PUT",
      body: user,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes customer profile.
     *
     * @tags Users
     * @name DeactivateUserUsingDelete
     * @summary Delete customer profile
     * @request DELETE:/{baseSiteId}/users/{userId}
     * @secure
     */
    this.deactivateUserUsingDelete = (baseSiteId, userId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Updates customer profile. Only attributes provided in the request body will be changed.
     *
     * @tags Users
     * @name UpdateUserUsingPatch1
     * @summary Updates customer profile
     * @request PATCH:/{baseSiteId}/users/{userId}
     * @secure
     */
    this.updateUserUsingPatch1 = (baseSiteId, userId, user, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}`,
      method: "PATCH",
      body: user,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns customer's addresses.
     *
     * @tags Users
     * @name GetAddressesUsingGet
     * @summary Get customer's addresses
     * @request GET:/{baseSiteId}/users/{userId}/addresses
     * @secure
     */
    this.getAddressesUsingGet = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Creates a new address.
     *
     * @tags Users
     * @name CreateAddressUsingPost
     * @summary Creates a new address.
     * @request POST:/{baseSiteId}/users/{userId}/addresses
     * @secure
     */
    this.createAddressUsingPost = (baseSiteId, userId, address, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses`,
      method: "POST",
      query,
      body: address,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Verifies address.
     *
     * @tags Users
     * @name VerifyAddressUsingPost1
     * @summary Verifies address
     * @request POST:/{baseSiteId}/users/{userId}/addresses/verification
     * @secure
     */
    this.verifyAddressUsingPost1 = (baseSiteId, userId, address, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses/verification`,
      method: "POST",
      query,
      body: address,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns detailed information about address with a given id.
     *
     * @tags Users
     * @name GetAddressUsingGet
     * @summary Get info about address
     * @request GET:/{baseSiteId}/users/{userId}/addresses/{addressId}
     * @secure
     */
    this.getAddressUsingGet = (addressId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses/${addressId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Updates the address. Attributes not provided in the request will be defined again (set to null or default).
     *
     * @tags Users
     * @name PutAddressUsingPut
     * @summary Updates the address
     * @request PUT:/{baseSiteId}/users/{userId}/addresses/{addressId}
     * @secure
     */
    this.putAddressUsingPut = (addressId, baseSiteId, userId, address, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses/${addressId}`,
      method: "PUT",
      body: address,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes customer's address.
     *
     * @tags Users
     * @name DeleteAddressUsingDelete
     * @summary Delete customer's address
     * @request DELETE:/{baseSiteId}/users/{userId}/addresses/{addressId}
     * @secure
     */
    this.deleteAddressUsingDelete = (addressId, baseSiteId, userId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses/${addressId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Updates the address. Only attributes provided in the request body will be changed.
     *
     * @tags Users
     * @name PatchAddressUsingPatch
     * @summary Updates the address
     * @request PATCH:/{baseSiteId}/users/{userId}/addresses/{addressId}
     * @secure
     */
    this.patchAddressUsingPatch = (addressId, baseSiteId, userId, address, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/addresses/${addressId}`,
      method: "PATCH",
      body: address,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Lists all customer carts.
     *
     * @tags Carts
     * @name GetCartsUsingGet
     * @summary Get all customer carts.
     * @request GET:/{baseSiteId}/users/{userId}/carts
     * @secure
     */
    this.getCartsUsingGet = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Creates a new cart or restores an anonymous cart as a user's cart (if an old Cart Id is given in the request).
     *
     * @tags Carts
     * @name CreateCartUsingPost
     * @summary Creates or restore a cart for a user.
     * @request POST:/{baseSiteId}/users/{userId}/carts
     * @secure
     */
    this.createCartUsingPost = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns the cart with a given identifier.
     *
     * @tags Carts
     * @name GetCartUsingGet
     * @summary Get a cart with a given identifier.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}
     * @secure
     */
    this.getCartUsingGet = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Deletes a cart with a given cart id.
     *
     * @tags Carts
     * @name DeleteCartUsingDelete
     * @summary Deletes a cart with a given cart id.
     * @request DELETE:/{baseSiteId}/users/{userId}/carts/{cartId}
     * @secure
     */
    this.deleteCartUsingDelete = (baseSiteId, userId, cartId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Creates an address and assigns it to the cart as the delivery address.
     *
     * @tags Carts
     * @name CreateAndSetAddressUsingPost1
     * @summary Creates a delivery address for the cart.
     * @request POST:/{baseSiteId}/users/{userId}/carts/{cartId}/addresses/delivery
     * @secure
     */
    this.createAndSetAddressUsingPost1 = (baseSiteId, userId, cartId, address, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/addresses/delivery`,
      method: "POST",
      query,
      body: address,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Sets a delivery address for the cart. The address country must be placed among the delivery countries of the current base store.
     *
     * @tags Carts
     * @name SetCartDeliveryAddressUsingPut
     * @summary Sets a delivery address for the cart.
     * @request PUT:/{baseSiteId}/users/{userId}/carts/{cartId}/addresses/delivery
     * @secure
     */
    this.setCartDeliveryAddressUsingPut = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/addresses/delivery`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes the delivery address from the cart.
     *
     * @tags Carts
     * @name RemoveCartDeliveryAddressUsingDelete
     * @summary Delete the delivery address from the cart.
     * @request DELETE:/{baseSiteId}/users/{userId}/carts/{cartId}/addresses/delivery
     * @secure
     */
    this.removeCartDeliveryAddressUsingDelete = (baseSiteId, userId, cartId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/addresses/delivery`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Explicitly clones a cart.
     *
     * @tags Save Cart
     * @name CloneSaveCartUsingPost
     * @summary Explicitly clones a cart.
     * @request POST:/{baseSiteId}/users/{userId}/carts/{cartId}/clonesavedcart
     * @secure
     */
    this.cloneSaveCartUsingPost = (cartId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/clonesavedcart`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns the delivery mode selected for the cart.
     *
     * @tags Carts
     * @name GetCartDeliveryModeUsingGet
     * @summary Get the delivery mode selected for the cart.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/deliverymode
     * @secure
     */
    this.getCartDeliveryModeUsingGet = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/deliverymode`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Sets the delivery mode with a given identifier for the cart.
     *
     * @tags Carts
     * @name SetCartDeliveryModeUsingPut
     * @summary Sets the delivery mode for a cart.
     * @request PUT:/{baseSiteId}/users/{userId}/carts/{cartId}/deliverymode
     * @secure
     */
    this.setCartDeliveryModeUsingPut = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/deliverymode`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes the delivery mode from the cart.
     *
     * @tags Carts
     * @name RemoveDeliveryModeUsingDelete
     * @summary Delete the delivery mode from the cart.
     * @request DELETE:/{baseSiteId}/users/{userId}/carts/{cartId}/deliverymode
     * @secure
     */
    this.removeDeliveryModeUsingDelete = (baseSiteId, userId, cartId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/deliverymode`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns all delivery modes supported for the current base store and cart delivery address. A delivery address must be set for the cart, otherwise an empty list will be returned.
     *
     * @tags Carts
     * @name GetSupportedDeliveryModesUsingGet
     * @summary Get all delivery modes for the current store and delivery address.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/deliverymodes
     * @secure
     */
    this.getSupportedDeliveryModesUsingGet = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/deliverymodes`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Assigns an email to the cart. This step is required to make a guest checkout.
     *
     * @tags Carts
     * @name GuestLoginUsingPut
     * @summary Assigns an email to the cart.
     * @request PUT:/{baseSiteId}/users/{userId}/carts/{cartId}/email
     * @secure
     */
    this.guestLoginUsingPut = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/email`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns cart entries.
     *
     * @tags Carts
     * @name GetCartEntriesUsingGet
     * @summary Get cart entries.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/entries
     * @secure
     */
    this.getCartEntriesUsingGet = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/entries`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Adds a product to the cart.
     *
     * @tags Carts
     * @name AddCartEntryUsingPost
     * @summary Adds a product to the cart.
     * @request POST:/{baseSiteId}/users/{userId}/carts/{cartId}/entries
     * @secure
     */
    this.addCartEntryUsingPost = (userId, cartId, baseSiteId, entry, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/entries`,
      method: "POST",
      query,
      body: entry,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns the details of the cart entries.
     *
     * @tags Carts
     * @name GetCartEntryUsingGet
     * @summary Get the details of the cart entries.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/entries/{entryNumber}
     * @secure
     */
    this.getCartEntryUsingGet = (entryNumber, baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/entries/${entryNumber}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Updates the quantity of a single cart entry and the details of the store where the cart entry will be picked up. Attributes not provided in request will be defined again (set to null or default)
     *
     * @tags Carts
     * @name SetCartEntryUsingPut1
     * @summary Set quantity and store details of a cart entry.
     * @request PUT:/{baseSiteId}/users/{userId}/carts/{cartId}/entries/{entryNumber}
     * @secure
     */
    this.setCartEntryUsingPut1 = (entryNumber, userId, cartId, baseSiteId, entry, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/entries/${entryNumber}`,
      method: "PUT",
      query,
      body: entry,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Deletes cart entry.
     *
     * @tags Carts
     * @name RemoveCartEntryUsingDelete
     * @summary Deletes cart entry.
     * @request DELETE:/{baseSiteId}/users/{userId}/carts/{cartId}/entries/{entryNumber}
     * @secure
     */
    this.removeCartEntryUsingDelete = (entryNumber, baseSiteId, userId, cartId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/entries/${entryNumber}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Updates the quantity of a single cart entry and the details of the store where the cart entry will be picked up.
     *
     * @tags Carts
     * @name UpdateCartEntryUsingPatch1
     * @summary Update quantity and store details of a cart entry.
     * @request PATCH:/{baseSiteId}/users/{userId}/carts/{cartId}/entries/{entryNumber}
     * @secure
     */
    this.updateCartEntryUsingPatch1 = (entryNumber, userId, cartId, baseSiteId, entry, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/entries/${entryNumber}`,
      method: "PATCH",
      query,
      body: entry,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Flags a cart for deletion (the cart doesn't have corresponding save cart attributes anymore). The cart is not actually deleted from the database. But with the removal of the saved cart attributes, this cart will be taken care of by the cart removal job just like any other cart.
     *
     * @tags Save Cart
     * @name FlagForDeletionUsingPatch
     * @summary Flag a cart for deletion.
     * @request PATCH:/{baseSiteId}/users/{userId}/carts/{cartId}/flagForDeletion
     * @secure
     */
    this.flagForDeletionUsingPatch = (cartId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/flagForDeletion`,
      method: "PATCH",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Defines the details of a new credit card, and assigns this payment option to the cart.
     *
     * @tags Carts
     * @name AddPaymentDetailsUsingPost
     * @summary Defines and assigns details of a new credit card payment to the cart.
     * @request POST:/{baseSiteId}/users/{userId}/carts/{cartId}/paymentdetails
     * @secure
     */
    this.addPaymentDetailsUsingPost = (baseSiteId, userId, cartId, paymentDetails, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/paymentdetails`,
      method: "POST",
      query,
      body: paymentDetails,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Sets credit card payment details for the cart.
     *
     * @tags Carts
     * @name SetPaymentDetailsUsingPut
     * @summary Sets credit card payment details for the cart.
     * @request PUT:/{baseSiteId}/users/{userId}/carts/{cartId}/paymentdetails
     * @secure
     */
    this.setPaymentDetailsUsingPut = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/paymentdetails`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns information about the promotions applied on the cart. Requests pertaining to promotions have been developed for the previous version of promotions and vouchers, and as a result, some of them are currently not compatible with the new promotions engine.
     *
     * @tags Carts
     * @name GetPromotionsUsingGet
     * @summary Get information about promotions applied on cart.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/promotions
     * @secure
     */
    this.getPromotionsUsingGet = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/promotions`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Enables a promotion for the order based on the promotionId defined for the cart. Requests pertaining to promotions have been developed for the previous version of promotions and vouchers, and as a result, some of them are currently not compatible with the new promotions engine.
     *
     * @tags Carts
     * @name ApplyPromotionUsingPost
     * @summary Enables promotions based on the promotionsId of the cart.
     * @request POST:/{baseSiteId}/users/{userId}/carts/{cartId}/promotions
     * @secure
     */
    this.applyPromotionUsingPost = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/promotions`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns information about a promotion (with a specific promotionId), that has been applied on the cart. Requests pertaining to promotions have been developed for the previous version of promotions and vouchers, and as a result, some of them are currently not compatible with the new promotions engine.
     *
     * @tags Carts
     * @name GetPromotionUsingGet
     * @summary Get information about promotions applied on cart.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/promotions/{promotionId}
     * @secure
     */
    this.getPromotionUsingGet = (promotionId, baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/promotions/${promotionId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Disables the promotion for the order based on the promotionId defined for the cart. Requests pertaining to promotions have been developed for the previous version of promotions and vouchers, and as a result, some of them are currently not compatible with the new promotions engine.
     *
     * @tags Carts
     * @name RemovePromotionUsingDelete
     * @summary Disables the promotion based on the promotionsId of the cart.
     * @request DELETE:/{baseSiteId}/users/{userId}/carts/{cartId}/promotions/{promotionId}
     * @secure
     */
    this.removePromotionUsingDelete = (promotionId, baseSiteId, userId, cartId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/promotions/${promotionId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Restore a saved cart.
     *
     * @tags Save Cart
     * @name RestoreSavedCartUsingPatch
     * @summary Restore a saved cart.
     * @request PATCH:/{baseSiteId}/users/{userId}/carts/{cartId}/restoresavedcart
     * @secure
     */
    this.restoreSavedCartUsingPatch = (cartId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/restoresavedcart`,
      method: "PATCH",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Explicitly saves a cart.
     *
     * @tags Save Cart
     * @name SaveCartUsingPatch
     * @summary Explicitly saves a cart.
     * @request PATCH:/{baseSiteId}/users/{userId}/carts/{cartId}/save
     * @secure
     */
    this.saveCartUsingPatch = (cartId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/save`,
      method: "PATCH",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns a saved cart for an authenticated user. The cart is identified using the "cartId" parameter.
     *
     * @tags Save Cart
     * @name GetSavedCartUsingGet
     * @summary Get a saved cart.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/savedcart
     * @secure
     */
    this.getSavedCartUsingGet = (cartId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/savedcart`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns a list of vouchers applied to the cart.
     *
     * @tags Carts
     * @name GetVouchersUsingGet
     * @summary Get a list of vouchers applied to the cart.
     * @request GET:/{baseSiteId}/users/{userId}/carts/{cartId}/vouchers
     * @secure
     */
    this.getVouchersUsingGet = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/vouchers`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Applies a voucher based on the voucherId defined for the cart.
     *
     * @tags Carts
     * @name ApplyVoucherForCartUsingPost
     * @summary Applies a voucher based on the voucherId defined for the cart.
     * @request POST:/{baseSiteId}/users/{userId}/carts/{cartId}/vouchers
     * @secure
     */
    this.applyVoucherForCartUsingPost = (baseSiteId, userId, cartId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/vouchers`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes a voucher based on the voucherId defined for the current cart.
     *
     * @tags Carts
     * @name ReleaseVoucherFromCartUsingDelete
     * @summary Delete a voucher defined for the current cart.
     * @request DELETE:/{baseSiteId}/users/{userId}/carts/{cartId}/vouchers/{voucherId}
     * @secure
     */
    this.releaseVoucherFromCartUsingDelete = (voucherId, baseSiteId, userId, cartId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/carts/${cartId}/vouchers/${voucherId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns all customer groups of a customer.
     *
     * @tags Users
     * @name GetAllCustomerGroupsForCustomerUsingGet
     * @summary Get all customer groups of a customer.
     * @request GET:/{baseSiteId}/users/{userId}/customergroups
     * @secure
     */
    this.getAllCustomerGroupsForCustomerUsingGet = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/customergroups`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Changes a customer's login name. Requires the customer's current password.
     *
     * @tags Users
     * @name ChangeLoginUsingPut
     * @summary Changes customer's login name.
     * @request PUT:/{baseSiteId}/users/{userId}/login
     * @secure
     */
    this.changeLoginUsingPut = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/login`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns order history data for all orders placed by a specified user for a specified base store. The response can display the results across multiple pages, if required.
     *
     * @tags Orders
     * @name GetOrdersForUserUsingGet
     * @summary Get order history for user
     * @request GET:/{baseSiteId}/users/{userId}/orders
     * @secure
     */
    this.getOrdersForUserUsingGet = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/orders`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description In the response header, the "x-total-count" indicates the total number of orders placed by a specified user for a specified base store.
     *
     * @tags Orders
     * @name GetCountOrdersForUserUsingHead
     * @summary Get total number of orders
     * @request HEAD:/{baseSiteId}/users/{userId}/orders
     * @secure
     */
    this.getCountOrdersForUserUsingHead = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/orders`,
      method: "HEAD",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Authorizes the cart and places the order. The response contains the new order data.
     *
     * @tags Orders
     * @name PlaceOrderUsingPost
     * @summary Post a order
     * @request POST:/{baseSiteId}/users/{userId}/orders
     * @secure
     */
    this.placeOrderUsingPost = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/orders`,
      method: "POST",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns specific order details based on a specific order code. The response contains detailed order information.
     *
     * @tags Orders
     * @name GetOrderForUserByCodeUsingGet
     * @summary Get a order
     * @request GET:/{baseSiteId}/users/{userId}/orders/{code}
     * @secure
     */
    this.getOrderForUserByCodeUsingGet = (code, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/orders/${code}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Changes customer's password.
     *
     * @tags Users
     * @name ChangePasswordUsingPut
     * @summary Changes customer's password
     * @request PUT:/{baseSiteId}/users/{userId}/password
     * @secure
     */
    this.changePasswordUsingPut = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/password`,
      method: "PUT",
      query,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Return customer's credit card payment details list.
     *
     * @tags Users
     * @name GetPaymentInfosUsingGet
     * @summary Get customer's credit card payment details list.
     * @request GET:/{baseSiteId}/users/{userId}/paymentdetails
     * @secure
     */
    this.getPaymentInfosUsingGet = (baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/paymentdetails`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Returns a customer's credit card payment details for the specified paymentDetailsId.
     *
     * @tags Users
     * @name GetPaymentDetailsUsingGet
     * @summary Get customer's credit card payment details.
     * @request GET:/{baseSiteId}/users/{userId}/paymentdetails/{paymentDetailsId}
     * @secure
     */
    this.getPaymentDetailsUsingGet = (paymentDetailsId, baseSiteId, userId, query, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/paymentdetails/${paymentDetailsId}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
    /**
     * @description Updates existing customer's credit card payment info based on the payment info ID. Attributes not given in request will be defined again (set to null or default).
     *
     * @tags Users
     * @name PutPaymentInfoUsingPut1
     * @summary Updates existing customer's credit card payment info.
     * @request PUT:/{baseSiteId}/users/{userId}/paymentdetails/{paymentDetailsId}
     * @secure
     */
    this.putPaymentInfoUsingPut1 = (paymentDetailsId, baseSiteId, userId, paymentDetails, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/paymentdetails/${paymentDetailsId}`,
      method: "PUT",
      body: paymentDetails,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Removes a customer's credit card payment details based on a specified paymentDetailsId.
     *
     * @tags Users
     * @name DeletePaymentInfoUsingDelete
     * @summary Delete customer's credit card payment details.
     * @request DELETE:/{baseSiteId}/users/{userId}/paymentdetails/{paymentDetailsId}
     * @secure
     */
    this.deletePaymentInfoUsingDelete = (paymentDetailsId, baseSiteId, userId, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/paymentdetails/${paymentDetailsId}`,
      method: "DELETE",
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Updates an existing customer's credit card payment details based on the specified paymentDetailsId. Only those attributes provided in the request will be updated.
     *
     * @tags Users
     * @name UpdatePaymentInfoUsingPatch
     * @summary Updates existing customer's credit card payment details.
     * @request PATCH:/{baseSiteId}/users/{userId}/paymentdetails/{paymentDetailsId}
     * @secure
     */
    this.updatePaymentInfoUsingPatch = (paymentDetailsId, baseSiteId, userId, paymentDetails, params = {}) => this.request({
      path: `/${baseSiteId}/users/${userId}/paymentdetails/${paymentDetailsId}`,
      method: "PATCH",
      body: paymentDetails,
      secure: true,
      type: "application/json" /* Json */,
      ...params
    });
    /**
     * @description Returns details of a single voucher that is specified by its voucher identification code.
     *
     * @tags Vouchers
     * @name GetVoucherByCodeUsingGet
     * @summary Get a voucher based on code
     * @request GET:/{baseSiteId}/vouchers/{code}
     * @secure
     */
    this.getVoucherByCodeUsingGet = (code, baseSiteId, query, params = {}) => this.request({
      path: `/${baseSiteId}/vouchers/${code}`,
      method: "GET",
      query,
      secure: true,
      type: "application/json" /* Json */,
      format: "json",
      ...params
    });
  }
};

// src/upload/http-client.ts
var HttpClient3 = class {
  constructor(apiConfig = {}) {
    this.baseUrl = "//hemkop.systest2.occdev.axfood.se/";
    this.securityData = null;
    this.abortControllers = /* @__PURE__ */ new Map();
    this.customFetch = (...fetchParams) => fetch(...fetchParams);
    this.baseApiParams = {
      credentials: "same-origin",
      headers: {},
      redirect: "follow",
      referrerPolicy: "no-referrer"
    };
    this.setSecurityData = (data) => {
      this.securityData = data;
    };
    this.contentFormatters = {
      ["application/json" /* Json */]: (input) => input !== null && (typeof input === "object" || typeof input === "string") ? JSON.stringify(input) : input,
      ["text/plain" /* Text */]: (input) => input !== null && typeof input !== "string" ? JSON.stringify(input) : input,
      ["multipart/form-data" /* FormData */]: (input) => Object.keys(input || {}).reduce((formData, key) => {
        const property = input[key];
        formData.append(
          key,
          property instanceof Blob ? property : typeof property === "object" && property !== null ? JSON.stringify(property) : `${property}`
        );
        return formData;
      }, new FormData()),
      ["application/x-www-form-urlencoded" /* UrlEncoded */]: (input) => this.toQueryString(input)
    };
    this.createAbortSignal = (cancelToken) => {
      if (this.abortControllers.has(cancelToken)) {
        const abortController2 = this.abortControllers.get(cancelToken);
        if (abortController2) {
          return abortController2.signal;
        }
        return void 0;
      }
      const abortController = new AbortController();
      this.abortControllers.set(cancelToken, abortController);
      return abortController.signal;
    };
    this.abortRequest = (cancelToken) => {
      const abortController = this.abortControllers.get(cancelToken);
      if (abortController) {
        abortController.abort();
        this.abortControllers.delete(cancelToken);
      }
    };
    this.request = async ({
      body,
      secure,
      path,
      type,
      query,
      format,
      baseUrl,
      cancelToken,
      ...params
    }) => {
      const secureParams = (typeof secure === "boolean" ? secure : this.baseApiParams.secure) && this.securityWorker && await this.securityWorker(this.securityData) || {};
      const requestParams = this.mergeRequestParams(params, secureParams);
      const queryString = query && this.toQueryString(query);
      const payloadFormatter = this.contentFormatters[type || "application/json" /* Json */];
      const responseFormat = format || requestParams.format;
      return this.customFetch(`${baseUrl || this.baseUrl || ""}${path}${queryString ? `?${queryString}` : ""}`, {
        ...requestParams,
        headers: {
          ...requestParams.headers || {},
          ...type && type !== "multipart/form-data" /* FormData */ ? { "Content-Type": type } : {}
        },
        signal: (cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal) || null,
        body: typeof body === "undefined" || body === null ? null : payloadFormatter(body)
      }).then(async (response) => {
        const r = response.clone();
        r.data = null;
        r.error = null;
        const data = !responseFormat ? r : await response[responseFormat]().then((data2) => {
          if (r.ok) {
            r.data = data2;
          } else {
            r.error = data2;
          }
          return r;
        }).catch((e) => {
          r.error = e;
          return r;
        });
        if (cancelToken) {
          this.abortControllers.delete(cancelToken);
        }
        if (!response.ok) throw data;
        return data;
      });
    };
    Object.assign(this, apiConfig);
  }
  encodeQueryParam(key, value) {
    const encodedKey = encodeURIComponent(key);
    return `${encodedKey}=${encodeURIComponent(typeof value === "number" ? value : `${value}`)}`;
  }
  addQueryParam(query, key) {
    return this.encodeQueryParam(key, query[key]);
  }
  addArrayQueryParam(query, key) {
    const value = query[key];
    return value.map((v) => this.encodeQueryParam(key, v)).join("&");
  }
  toQueryString(rawQuery) {
    const query = rawQuery || {};
    const keys = Object.keys(query).filter((key) => "undefined" !== typeof query[key]);
    return keys.map((key) => Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key)).join("&");
  }
  addQueryParams(rawQuery) {
    const queryString = this.toQueryString(rawQuery);
    return queryString ? `?${queryString}` : "";
  }
  mergeRequestParams(params1, params2) {
    return {
      ...this.baseApiParams,
      ...params1,
      ...params2 || {},
      headers: {
        ...this.baseApiParams.headers || {},
        ...params1.headers || {},
        ...params2 && params2.headers || {}
      }
    };
  }
};

// src/upload/Axfood.ts
var Axfood2 = class extends HttpClient3 {
  constructor() {
    super(...arguments);
    /**
     * No description
     *
     * @tags Customer service
     * @name HandleIncomingRequestUsingPost
     * @summary Handle incoming request
     * @request POST:/axfood/rest/customer-service/send
     */
    this.handleIncomingRequestUsingPost = (data, params = {}) => this.request({
      path: `/axfood/rest/customer-service/send`,
      method: "POST",
      body: data,
      type: "multipart/form-data" /* FormData */,
      ...params
    });
  }
};
export {
  ArticleNavigationMenuComponent as ArticleNavigationApi,
  Axfood as AxfoodApi,
  BuyAllProductsComponent as BuyAllProductsApi,
  C as CategoryApi,
  Singlestepcheckout as CheckoutApi,
  BaseSiteId as CmsApi,
  ContentType,
  Axfood2 as CustomerServiceApi,
  Customers as CustomersApi,
  LeftMenu as LeftMenuApi,
  Login as LoginApi,
  ProductBannerComponent as ProductBannerApi,
  ProductCarouselComponent as ProductCarouselApi,
  Search as SearchApi,
  TwoTilesButtonAndProductBannerComponent as TwoTilesApi
};
